import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  actionsApi,
  getKnowledgeGeneratorTasks,
  getKnowledgeGeneratorTask,
  openKnowledgeGeneratorDraft,
  getKnowledgeGeneratorRepRuntimeReadiness,
  getPlaybookIngestionTask,
  publishKnowledgeGeneratorTask,
  mergeKnowledgeGeneratorObjectionVariation,
  persistKnowledgeGeneratorOnboardingSelection,
  persistKnowledgeGeneratorDraftRemoval,
  persistKnowledgeGeneratorStarterPlaybookSelection,
  reviewKnowledgeGeneratorLandmine,
  updateKnowledgeGeneratorLandmineDraft,
  reviewKnowledgeGeneratorCompetitor,
  retryKnowledgeGeneratorStarterPlaybook,
  generateKnowledgeGeneratorSuggestionVariations,
  generateLandmineVariations,
  updateKnowledgeGeneratorSuggestion,
  updateKnowledgeGeneratorCompanyProfile,
  markKnowledgeGeneratorClaimsReviewed,
  getLandmines,
  getCompetitors,
  getKnowledgeGeneratorDraftDuplicates,
  keepBothKnowledgeGeneratorDraftDuplicates,
  type KnowledgeGeneratorTask,
  type KnowledgeGeneratorRepRuntimeReadiness,
  type KnowledgeGeneratorLandmineDraft,
  type KnowledgeGeneratorCompetitorDraft,
  type LandmineItem,
  type CompetitorItem,
  type DraftDuplicatePair,
  type PlaybookIngestionTask,
} from '../services/apiService';
import { TopicCategory, TOPIC_CATEGORY_LABELS } from '../constants/topicCategories';
import { CompetitorCategory } from '../types/competitor';
import type { ImproveItemCurrent, OnboardingReviewSection, OnboardingStage, SmartWizardRequest } from '../types/onboarding';
import OnboardingWizard, { type CapturedItem } from './OnboardingWizard';
import PlaybookIngestionReview from './PlaybookIngestionReview';
import { useConfirm } from './ui/ConfirmDialog';
import { Dropdown } from './ui/Dropdown';
import BulkUploadModal from './kb/BulkUploadModal';
import type { DraftPreviewItem } from './onboarding/draftSearchMatcher';
import DraftKnowledgeSearch from './onboarding/DraftKnowledgeSearch';
import CompetitorDraftCard, { competitorDraftVersion } from './onboarding/CompetitorDraftCard';
import ObjectionVariationMergeDialog from './onboarding/ObjectionVariationMergeDialog';
import { hasUnresolvedUncertaintyLabel } from '../utils/uncertaintyLabels';
import { isSuperadminInitialDraft } from '../utils/knowledgeTaskOrigin';
import {
  buildCompanyProfileDraft,
  isPublishableSuggestion,
  markStarterSet,
  mergeLandmineVariations,
  mergePolishedSuggestions,
  partitionReviewSuggestions,
  reviewSignature,
  sectionOfKind,
  stripReviewFields,
  toReviewList,
  type CompanyProfileDraft,
  type ProfileFieldKey,
  type ReviewSuggestion,
  type SuggestionKind,
} from './seededOnboardingReviewState';

type LandmineReviewContent = Pick<KnowledgeGeneratorLandmineDraft, 'trigger' | 'landmineType'>
  & Partial<Pick<KnowledgeGeneratorLandmineDraft, 'triggerVariations' | 'canSay' | 'cannotSay' | 'quickTips'>>;

export const landmineDraftVersion = (draft: LandmineReviewContent) => JSON.stringify({
  trigger: draft.trigger || '',
  triggerVariations: [...new Set(draft.triggerVariations || [])].sort(),
  landmineType: draft.landmineType || 'legal_risk',
  canSay: (draft.canSay || []).map(({ point, context }) => ({ point, context: context || '' })),
  cannotSay: (draft.cannotSay || []).map(({ point, reason, severity }) => ({ point, reason: reason || '', severity: severity || 'general' })),
  quickTips: draft.quickTips || '',
});

const mergeLandmineEditedPoints = <T extends { point: string }>(
  text: string,
  existing: T[],
  create: (point: string) => T,
): T[] => {
  // A line is the editor's record boundary. Preserve the actual line value;
  // trimming it here would mutate protected wording before the API sees it.
  const points = text.split('\n').filter((point) => point.trim());
  const unusedIndexes = new Set(existing.map((_, index) => index));

  return points.map((point, position) => {
    const exactIndex = existing.findIndex((item, index) => (
      unusedIndexes.has(index) && item.point === point
    ));
    const sourceIndex = exactIndex >= 0
      ? exactIndex
      : (unusedIndexes.has(position) ? position : -1);
    if (sourceIndex < 0) return create(point);
    unusedIndexes.delete(sourceIndex);
    return { ...existing[sourceIndex], point };
  });
};

const landmineSetVersion = (items: KnowledgeGeneratorLandmineDraft[]) => JSON.stringify(
  items.map((draft) => ({
    tempId: draft.tempId,
    version: landmineDraftVersion(draft),
  })).sort((left, right) => left.tempId.localeCompare(right.tempId))
);

const competitorSetVersion = (items: KnowledgeGeneratorCompetitorDraft[]) => JSON.stringify(
  items.map((draft) => ({
    tempId: draft.tempId,
    version: competitorDraftVersion(draft),
  })).sort((left, right) => left.tempId.localeCompare(right.tempId))
);

/**
 * Review-first onboarding surface for a draft explicitly prepared by a
 * superadmin (plus legacy invite-time drafts). Unlike OnboardingWizard,
 * this screen shows what Sales Guru already drafted from public information
 * — a KnowledgeGeneratorTask built from the supplied research inputs — and
 * lets the admin approve a small starter set before anything touches the
 * live objections/faqs collections (which feed FAISS + Algolia) or the
 * organization's company-context settings.
 *
 * Trust boundary, stated plainly to the admin: nothing generated here is
 * live until "Publish starter set" succeeds. Review edits and publication
 * use the canonical knowledge-generator task endpoints; there is no second
 * sync pathway.
 */
export interface SeededOnboardingReviewProps {
  /** Done reviewing (published or chose to leave) — return to the plain KB page. */
  onExit: () => void;
  taskId?: string;
  /** The KB page's own "most recent active draft of any provenance"
   *  (readiness.activeDraftTask), tried when there's no explicit `taskId`
   *  and no superadmin-seeded draft — see the initial-load effect below.
   *  Distinct from a *seeded* draft: this is "any draft that's still open",
   *  the same one the old `?setup=start` route used to resume the wizard
   *  against directly. */
  fallbackDraftTaskId?: string;
  /** Deep link from the shared readiness/Guide contract. */
  initialReviewSection?: OnboardingReviewSection;
}

type Phase = 'loading' | 'loadError' | 'emptyReview' | 'preparing' | 'review' | 'completed' | 'alreadyPublished' | 'fallback';

type AssistantSection = OnboardingStage;

type ActiveGuideTarget =
  | { type: 'suggestion'; kind: SuggestionKind; tempId: string }
  | { type: 'profile'; field: ProfileFieldKey };

interface VariationMergeRequest {
  sourceTempId: string;
  source: ReviewSuggestion;
  initialTargetTempId?: string;
}

/** Five simple cards instead of one dense paragraph — each maps to one
 *  existing settings field (no schema change beyond adding `targetAudience`
 *  alongside the four that already existed). */
const PROFILE_FIELDS: { key: ProfileFieldKey; label: string; emptyHint?: string }[] = [
  { key: 'whatYouSell', label: 'What you sell' },
  { key: 'targetAudience', label: 'Who buys it', emptyHint: 'Not found in public research. Add this yourself, or leave blank.' },
  { key: 'differentiators', label: 'Why customers choose you' },
  { key: 'howItWorks', label: 'How it works', emptyHint: 'Not found in public research. Add this yourself, or leave blank.' },
  { key: 'certifications', label: 'Claims to verify', emptyHint: 'None found.' },
];

const PROFILE_TO_RESEARCH_FIELD: Record<ProfileFieldKey, string> = {
  whatYouSell: 'summary',
  targetAudience: 'targetAudience',
  howItWorks: 'howItWorks',
  certifications: 'certifications',
  differentiators: 'keyFeatures',
};

/** Grows a textarea to fit its content instead of clipping into an internal
 *  scrollbar — used as an inline ref callback so it re-runs on every render
 *  (including the first, once `value` is populated from the fetched draft). */
const autoGrowTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};
const POLL_INTERVAL_MS = 5000;
const CLEANUP_POLL_INTERVAL_MS = 1200;
const PLAYBOOK_POLL_INTERVAL_MS = 3000;
const PLAYBOOK_IN_PROGRESS_STATUSES = new Set(['pending', 'locking_protected_content', 'extracting', 'generating', 'cleaning']);

const retainedDraftCount = (task: KnowledgeGeneratorTask): number => {
  if (task.unpublishedDraftCount != null) return task.unpublishedDraftCount;
  return [
    ...(task.suggestions?.objections || []),
    ...(task.suggestions?.websiteFaqs || []),
    ...(task.suggestions?.aiGeneratedFaqs || []),
    ...(task.onboardingDraft?.landmines || []),
    ...(task.onboardingDraft?.competitors || []),
  ].filter((item) => !item.synced).length;
};

const isAdditiveReviewTask = (task: KnowledgeGeneratorTask): boolean => Boolean(
  task.generationMode === 'rescan' ||
  task.status === 'completed' ||
  task.syncResults?.syncedAt ||
  [
    ...(task.suggestions?.objections || []),
    ...(task.suggestions?.websiteFaqs || []),
    ...(task.suggestions?.aiGeneratedFaqs || []),
    ...(task.onboardingDraft?.landmines || []),
    ...(task.onboardingDraft?.competitors || []),
  ].some((item) => item.synced)
);

const unpublishedDraftItems = <T extends { synced?: boolean; removed?: boolean }>(items: T[] | undefined): T[] =>
  (items || []).filter((item) => !item.synced && !item.removed);

const CATEGORY_OPTIONS = Object.values(TopicCategory);

/** A removed suggestion, held long enough to put back. */
interface RemovedSuggestion {
  section: 'objections' | 'faqs';
  item: ReviewSuggestion;
  /** Where it sat in its section, so undo restores it in place. */
  index: number;
}

/** Same reversible-removal shape as `RemovedSuggestion`, for the landmine
 *  and competitor draft lists, which aren't `ReviewSuggestion`s. */
interface RemovedDraftItem<T> {
  item: T;
  /** Where it sat in its list, so undo restores it in place. */
  index: number;
}

/** Marks the review column so a section jump can find the element it needs to
 *  scroll, without coupling to the column's layout classes. */
const REVIEW_SCROLLER_ATTR = 'data-review-scroller';

/**
 * Puts a section at the top of the review column.
 *
 * Plain `scrollTop` assignment, deliberately, after two prettier approaches
 * failed to move the column at all:
 *
 *  - Native smooth scrolling (`scrollIntoView({ behavior: 'smooth' })` and
 *    `scrollTo({ behavior: 'smooth' })` alike) is a silent no-op on this
 *    container in at least one Chromium build — the rail's "click to jump"
 *    updated its highlight while the column never moved. The identical call
 *    with `behavior: 'auto'` works, so it is smooth support specifically.
 *  - A hand-rolled requestAnimationFrame tween inherits a different failure:
 *    rAF is throttled to zero in a backgrounded tab, so the scroll simply
 *    never completes.
 *
 * A jump that silently doesn't jump is a worse bug than one that doesn't
 * animate, and `scrollTop` is honoured by every engine in every tab state.
 */
const scrollSectionIntoView = (el: HTMLElement | null): void => {
  if (!el) return;
  const scroller = el.closest(`[${REVIEW_SCROLLER_ATTR}]`) as HTMLElement | null;
  if (!scroller) {
    el.scrollIntoView({ block: 'start' });
    return;
  }
  // Rect-based rather than offsetTop: the section may sit inside a
  // positioned wrapper, which would make offsetTop relative to the wrong box.
  const offsetWithinScroller = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  // Top-aligned with a little breathing room above, rather than centred:
  // these sections are frequently taller than the column, and centring a
  // tall section pushes its heading off the top of the screen.
  const target = scroller.scrollTop + offsetWithinScroller - 16;
  scroller.scrollTop = Math.max(0, Math.min(target, scroller.scrollHeight - scroller.clientHeight));
};


const chipClass = {
  category: 'bg-surface-subtle text-ink-secondary',
  website: 'bg-success-tint text-success border border-transparent',
  aiDrafted: 'bg-warning-tint text-warning border border-transparent',
  voice: 'bg-info-tint text-info border border-transparent',
  pending: 'bg-info-tint text-info border border-transparent',
  published: 'bg-success-tint text-success border border-transparent',
  edited: 'bg-info-tint text-info border border-transparent',
  polishing: 'bg-info-tint text-info border border-transparent',
  polished: 'bg-success-tint text-success border border-transparent',
  cleanupError: 'bg-surface-subtle text-ink-secondary border border-hairline',
  highConfidence: 'bg-success-tint text-success',
  needsReview: 'bg-warning-tint text-warning',
  unscored: 'bg-surface-sunken text-ink-secondary',
  duplicate: 'bg-error-tint text-error border border-transparent',
};

const toDraftPreviewItem = (item: ReviewSuggestion): DraftPreviewItem => ({
  tempId: item.tempId,
  question: item.question,
  answer: item.answer,
  type: item.kind === 'objection' ? 'objection' : 'faq',
});

/**
 * The draft-preview half of kb/QuickTestBox, reused here rather than forked:
 * shares `findDraftMatches` (and the `DraftPreviewItem` shape) so a query is
 * always scored the same way regardless of which screen ran it.
 *
 * `items` is built by the caller from this component's own live
 * `objections`/`faqs` state, not by calling QuickTestBox's
 * `selectedDraftAnswers(task)` — that reads `task.suggestions`, which only
 * reflects a toggle or edit made in this review session after the next
 * server round-trip, while the local review state here is already
 * authoritative for what is currently selected/edited. Only the matching
 * algorithm is shared; the input list is deliberately not.
 *
 * The full QuickTestBox (with its published-index search half) is not reused
 * as-is: that half needs `liveAnswerCount`, which would mean either an extra
 * network fetch this screen doesn't otherwise make or threading a new prop
 * through KnowledgeBase.tsx — for "does this drafted answer sound right"
 * neither is worth it, since nothing here is published yet anyway.
 */
const SeededOnboardingReview: React.FC<SeededOnboardingReviewProps> = ({ onExit, taskId, fallbackDraftTaskId, initialReviewSection }) => {
  const confirm = useConfirm();
  const [phase, setPhase] = useState<Phase>('loading');
  const [task, setTask] = useState<KnowledgeGeneratorTask | null>(null);
  const [preparingMessage, setPreparingMessage] = useState<string>('Preparing your draft…');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  const [objections, setObjections] = useState<ReviewSuggestion[]>([]);
  const [faqs, setFaqs] = useState<ReviewSuggestion[]>([]);
  const [selectionSaveError, setSelectionSaveError] = useState<string | null>(null);
  const [selectionRetry, setSelectionRetry] = useState<(() => void) | null>(null);
  const [savingSelectionKeys, setSavingSelectionKeys] = useState<Set<string>>(new Set());
  const selectionMutationRef = useRef(new Map<string, number>());
  const savingLandmineRef = useRef(false);
  const [reviewingLandmineId, setReviewingLandmineId] = useState<string | null>(null);
  const [reviewingCompetitorId, setReviewingCompetitorId] = useState<string | null>(null);
  const [editingLandmineId, setEditingLandmineId] = useState<string | null>(null);
  const [savingLandmine, setSavingLandmine] = useState(false);
  const [landmineEdit, setLandmineEdit] = useState({ trigger: '', canSay: '', cannotSay: '', variations: '' });
  const [companyProfile, setCompanyProfile] = useState<CompanyProfileDraft | null>(null);
  const [profileEditedFields, setProfileEditedFields] = useState<Set<ProfileFieldKey>>(new Set());
  const [draftLandmines, setDraftLandmines] = useState<KnowledgeGeneratorLandmineDraft[]>([]);
  const [draftCompetitors, setDraftCompetitors] = useState<KnowledgeGeneratorCompetitorDraft[]>([]);

  /** Most recent first. Cleared only by undoing or by publishing, so a
   *  removal stays reversible for as long as the admin is on this screen. */
  const [removedSuggestions, setRemovedSuggestions] = useState<RemovedSuggestion[]>([]);
  const [removedLandmines, setRemovedLandmines] = useState<RemovedDraftItem<KnowledgeGeneratorLandmineDraft>[]>([]);
  const [removedCompetitors, setRemovedCompetitors] = useState<RemovedDraftItem<KnowledgeGeneratorCompetitorDraft>[]>([]);
  const [expandedLandmineVariations, setExpandedLandmineVariations] = useState<Set<string>>(new Set());
  const [showAllLandmines, setShowAllLandmines] = useState(false);
  const [showAllCompetitors, setShowAllCompetitors] = useState(false);

  const [editing, setEditing] = useState<{ kind: SuggestionKind; tempId: string } | null>(null);
  const [editForm, setEditForm] = useState({
    question: '',
    answer: '',
    category: TopicCategory.OTHER as string,
    variations: '',
  });
  const [generatingEditVariations, setGeneratingEditVariations] = useState(false);
  const [editGenerationError, setEditGenerationError] = useState<string | null>(null);

  // The review owns persistence; the persistent smart wizard owns every
  // conversational add/improve interaction.
  const [fieldWriting, setFieldWriting] = useState<Partial<Record<ProfileFieldKey, boolean>>>({});
  const [markingClaimsReviewed, setMarkingClaimsReviewed] = useState(false);
  const [wizardRequest, setWizardRequest] = useState<SmartWizardRequest | null>(null);
  const [activeGuideTarget, setActiveGuideTarget] = useState<ActiveGuideTarget | null>(null);
  const wizardRequestId = useRef(0);
  const savingEditRef = useRef(false);
  const publishingRef = useRef(false);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [showPublishChecklist, setShowPublishChecklist] = useState(false);
  const [publishedCounts, setPublishedCounts] = useState({
    objections: 0,
    faqs: 0,
    companyProfile: false,
    landmines: 0,
    competitors: 0,
  });
  const [publishWarnings, setPublishWarnings] = useState<string[]>([]);
  const [repRuntimeReadiness, setRepRuntimeReadiness] = useState<KnowledgeGeneratorRepRuntimeReadiness | null>(null);
  const [draftDuplicates, setDraftDuplicates] = useState<DraftDuplicatePair[]>([]);
  /** A pair the admin explicitly reviewed and chose to keep both items for —
   *  the required resolution before two conflicting drafts can publish
   *  together. Keyed by tempIds so an edit that changes the wording doesn't
   *  silently keep a stale acknowledgement (the pair simply won't be
   *  reported by the server again until it re-detects a conflict). */
  const [acknowledgedDuplicatePairs, setAcknowledgedDuplicatePairs] = useState<Set<string>>(new Set());
  const [savingDuplicatePairKeys, setSavingDuplicatePairKeys] = useState<Set<string>>(new Set());
  const [variationMergeRequest, setVariationMergeRequest] = useState<VariationMergeRequest | null>(null);
  const [isMergingVariation, setIsMergingVariation] = useState(false);
  const [variationMergeError, setVariationMergeError] = useState<string | null>(null);

  // The draft carries unpublished landmines/competitors, while readiness is
  // based on the published collections. Fetch those live counts separately.
  const [landmineCounts, setLandmineCounts] = useState<{ total: number; pending: number } | null>(null);
  const [competitorCounts, setCompetitorCounts] = useState<{ total: number; pending: number } | null>(null);

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const [showPlaybookReview, setShowPlaybookReview] = useState(false);
  const [playbookMessage, setPlaybookMessage] = useState<string | null>(null);
  const [startPlaybookUpload, setStartPlaybookUpload] = useState(false);
  const [retryingStarterPlaybook, setRetryingStarterPlaybook] = useState(false);
  const [starterPlaybook, setStarterPlaybook] = useState<PlaybookIngestionTask | null>(null);
  const [includeStarterPlaybook, setIncludeStarterPlaybook] = useState(true);
  const additiveReview = task ? isAdditiveReviewTask(task) : false;

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runtimePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Signature of the draft as the server last handed it over — see
   *  `reviewSignature`. Null until the first task loads. */
  const pristineSignatureRef = useRef<string | null>(null);
  const sectionRefs = useRef<Record<AssistantSection, HTMLElement | null>>({
    company_context: null,
    objections: null,
    faqs: null,
    landmines: null,
    competitors: null,
  });

  /** Review navigation never remounts or redirects the conversation. */
  const focusAssistantSection = useCallback((section: AssistantSection) => {
    scrollSectionIntoView(sectionRefs.current[section]);
  }, []);

  const requestGuideForSection = useCallback((section: AssistantSection, pendingQuestion?: string) => {
    setActiveGuideTarget(null);
    setWizardRequest({ id: ++wizardRequestId.current, kind: 'add', stage: section, pendingQuestion });
  }, []);

  const selectAssistantSection = useCallback((section: AssistantSection) => {
    focusAssistantSection(section);
  }, [focusAssistantSection]);

  const goToSixCardSection = useCallback((section: AssistantSection) => {
    focusAssistantSection(section);
  }, [focusAssistantSection]);

  useEffect(() => {
    if (phase !== 'review' || !initialReviewSection) return;
    if (initialReviewSection === 'playbooks') {
      setShowPlaybookReview(true);
      return;
    }
    // Claims are part of the structured company-details section. The target
    // is intentionally the editor, never a legacy freeform context field.
    focusAssistantSection(initialReviewSection === 'claims' ? 'company_context' : initialReviewSection);
  }, [focusAssistantSection, initialReviewSection, phase]);

  const handleGuideItemCaptured = useCallback((item: CapturedItem) => {
    if (item.kind === 'company_context') {
      const draft: any = item.draft;
      const field = draft.field as ProfileFieldKey;
      if (PROFILE_FIELDS.some(({ key }) => key === field)) {
        setCompanyProfile((prev) => ({
          whatYouSell: prev?.whatYouSell || '',
          targetAudience: prev?.targetAudience || '',
          howItWorks: prev?.howItWorks || '',
          certifications: prev?.certifications || [],
          claimsReviewedAt: prev?.claimsReviewedAt || null,
          claimsReviewedByEmail: prev?.claimsReviewedByEmail || null,
          claimsReviewedVersion: prev?.claimsReviewedVersion || null,
          differentiators: prev?.differentiators || [],
          include: prev?.include ?? true,
          [field]: draft.value,
          ...(field === 'certifications' ? { claimsReviewedAt: null, claimsReviewedByEmail: null, claimsReviewedVersion: null } : {}),
        }));
        setProfileEditedFields((prev) => {
          const next = new Set(prev);
          next.delete(field);
          return next;
        });
        const researchField = PROFILE_TO_RESEARCH_FIELD[field];
        setTask((prev) => prev?.researchContent ? {
          ...prev,
          researchContent: {
            ...prev.researchContent,
            styleCleanupStatus: 'pending',
            styleCleanupPendingFields: [
              ...new Set([...(prev.researchContent.styleCleanupPendingFields || []), researchField]),
            ],
            styleCleanedFields: (prev.researchContent.styleCleanedFields || []).filter((value) => value !== researchField),
          },
        } : prev);
      }
    }
    if (item.kind === 'objections' || item.kind === 'faqs') {
      const draft: any = item.draft;
      const captureSource = draft.source === 'voice' ? 'voice' : 'manual';
      const capturedSuggestion: ReviewSuggestion = {
        tempId: draft.documentId || `voice-${item.id}`,
        documentId: draft.documentId,
        question: draft.question || '',
        answer: draft.answer || '',
        category: draft.category || TopicCategory.OTHER,
        variations: (draft.variations || []).map((variation: any) => ({
          tempId: variation.documentId || variation.text,
          question: variation.text,
        })),
        source: captureSource,
        styleCleanupStatus: draft.styleCleanupStatus || 'pending',
        styleCleanedAt: draft.styleCleanedAt || null,
        approved: true,
        edited: false,
        synced: false,
        kind: item.kind === 'objections' ? 'objection' : 'aiGeneratedFaq',
        voiceCaptured: captureSource === 'voice',
      };
      const replaceVoicePending = (list: ReviewSuggestion[]) => [
        capturedSuggestion,
        ...list.filter((existing) => !(existing.pending && existing.kind === capturedSuggestion.kind)),
      ];
      if (item.kind === 'objections') setObjections(replaceVoicePending);
      else setFaqs(replaceVoicePending);
    }
    if (item.kind === 'landmines') {
      const draft: any = item.draft;
      const landmine: KnowledgeGeneratorLandmineDraft = {
        tempId: draft.documentId || item.id,
        trigger: draft.trigger || 'New Landmine',
        triggerVariations: draft.triggerVariations || [],
        landmineType: draft.landmineType || 'legal_risk',
        category: draft.category || 'general',
        priority: draft.priority || 'medium',
        canSay: draft.canSay || [],
        cannotSay: draft.cannotSay || [],
        approvalStatus: 'pending',
        styleCleanupStatus: draft.styleCleanupStatus || 'pending',
        styleCleanedAt: draft.styleCleanedAt || null,
        approved: true,
        edited: false,
        synced: false,
        syncedAt: null,
      };
      setDraftLandmines((prev) => [landmine, ...prev.filter((existing) => existing.tempId !== landmine.tempId)]);
    } else if (item.kind === 'competitors') {
      const draft: any = item.draft;
      const competitor: KnowledgeGeneratorCompetitorDraft = {
        tempId: draft.documentId || item.id,
        name: draft.name || 'New competitor',
        advantages: (draft.advantages || []).map((point: string) => ({ point, category: CompetitorCategory.OTHER })),
        weaknesses: (draft.weaknesses || []).map((point: string) => ({ point, category: CompetitorCategory.OTHER })),
        talking_points: draft.talking_points || '',
        category: draft.category || 'direct_competitor',
        priority: draft.priority || 'medium',
        styleCleanupStatus: draft.styleCleanupStatus || 'pending',
        styleCleanedAt: draft.styleCleanedAt || null,
        approved: true,
        edited: false,
        synced: false,
        syncedAt: null,
      };
      setDraftCompetitors((prev) => [competitor, ...prev.filter((existing) => existing.tempId !== competitor.tempId)]);
    }
  }, []);

  const handleGuideItemPending = useCallback((item: CapturedItem) => {
    if (item.kind !== 'objections' && item.kind !== 'faqs') return;
    const draft: any = item.draft;
    const pendingSuggestion: ReviewSuggestion = {
      tempId: `voice-pending-${item.kind}`,
      question: draft.question || '',
      answer: '',
      category: draft.category || TopicCategory.OTHER,
      variations: [],
      approved: false,
      edited: false,
      synced: false,
      kind: item.kind === 'objections' ? 'objection' : 'aiGeneratedFaq',
      voiceCaptured: draft.source === 'voice',
      pending: true,
    };
    const replacePending = (list: ReviewSuggestion[]) => [
      pendingSuggestion,
      ...list.filter((existing) => !(existing.voiceCaptured && existing.pending && existing.kind === pendingSuggestion.kind)),
    ];
    if (item.kind === 'objections') setObjections(replacePending);
    else setFaqs(replacePending);
  }, []);

  /**
   * @param seedStarterSet Pick the initial selection from the task's drafts.
   *   True on first load. False when re-reading the task *after* a publish
   *   attempt: by then the admin's own ticks are what the server holds, and
   *   re-seeding would silently overwrite them with the default choice.
   */
  const applyTask = useCallback((full: KnowledgeGeneratorTask, seedStarterSet = true) => {
    const additive = isAdditiveReviewTask(full);
    const onlyUnpublished = (items: ReviewSuggestion[]) => items.filter((item) => !item.synced && !item.removed);
    const seed = (items: ReviewSuggestion[]) => (seedStarterSet && !additive ? markStarterSet(items) : items);
    const nextObjections = seed(onlyUnpublished(toReviewList(full.suggestions?.objections, 'objection')));
    // The admin sees a single FAQs section, so seed after the sources merge.
    const nextFaqs = seed(onlyUnpublished([
      ...toReviewList(full.suggestions?.websiteFaqs, 'websiteFaq'),
      ...toReviewList(full.suggestions?.aiGeneratedFaqs, 'aiGeneratedFaq'),
    ]));
    const nextProfile = additive ? null : buildCompanyProfileDraft(full.researchContent);
    const nextLandmines = unpublishedDraftItems(full.onboardingDraft?.landmines);
    const nextCompetitors = unpublishedDraftItems(full.onboardingDraft?.competitors);
    setTask(full);
    setObjections(nextObjections);
    setFaqs(nextFaqs);
    setCompanyProfile(nextProfile);
    setProfileEditedFields(new Set());
    setDraftLandmines(nextLandmines);
    setDraftCompetitors(nextCompetitors);
    setIncludeStarterPlaybook(full.onboardingDraft?.starterPlaybookIncluded !== false);
    // Baseline for the unsaved-work check on Close. Taken here because this
    // is the one place state is (re)seeded from the server, so everything
    // that diverges from it afterwards is the admin's own work — which lives
    // only in this component until Publish and would otherwise be discarded
    // silently.
    pristineSignatureRef.current = reviewSignature(nextObjections, nextFaqs, nextProfile, nextLandmines, nextCompetitors);
  }, []);

  useEffect(() => {
    const starterTaskId = task?.onboardingDraft?.starterPlaybookTaskId;
    if (!starterTaskId || phase !== 'review') {
      setStarterPlaybook(null);
      return;
    }
    let cancelled = false;
    getPlaybookIngestionTask(starterTaskId)
      .then((playbook) => {
        if (!cancelled) setStarterPlaybook(playbook);
      })
      .catch((error) => {
        if (!cancelled) {
          setStarterPlaybook(null);
          setSelectionSaveError(error instanceof Error ? error.message : 'Could not load the starter playbook draft.');
        }
      });
    return () => { cancelled = true; };
  }, [phase, task?.onboardingDraft?.starterPlaybookTaskId]);

  useEffect(() => {
    const starterTaskId = task?.onboardingDraft?.starterPlaybookTaskId;
    if (
      phase !== 'review' ||
      !starterTaskId ||
      !starterPlaybook ||
      !PLAYBOOK_IN_PROGRESS_STATUSES.has(starterPlaybook.status)
    ) return undefined;

    let cancelled = false;
    const interval = setInterval(() => {
      getPlaybookIngestionTask(starterTaskId)
        .then((playbook) => {
          if (!cancelled) setStarterPlaybook(playbook);
        })
        .catch((error) => {
          if (!cancelled) {
            setSelectionSaveError(error instanceof Error ? error.message : 'Could not refresh the starter playbook draft.');
          }
        });
    }, PLAYBOOK_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [phase, starterPlaybook, task?.onboardingDraft?.starterPlaybookTaskId]);

  const handleStarterPlaybookSelection = useCallback(async (included: boolean) => {
    if (!task) return;
    const previous = includeStarterPlaybook;
    setIncludeStarterPlaybook(included);
    setSelectionSaveError(null);
    try {
      const updated = await persistKnowledgeGeneratorStarterPlaybookSelection(task._id, included);
      setTask(updated);
    } catch (error) {
      setIncludeStarterPlaybook(previous);
      setSelectionSaveError(
        `Choice was not saved: ${error instanceof Error ? error.message : 'please try again.'}`
      );
    }
  }, [includeStarterPlaybook, task]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const loadFromTaskSummary = useCallback(
    async (summary: KnowledgeGeneratorTask) => {
      if (['pending', 'researching', 'generating'].includes(summary.status)) {
        setTask(summary);
        setPreparingMessage(summary.progress?.message || 'Preparing your draft…');
        setPhase('preparing');
        return;
      }

      if (summary.status === 'error') {
        // Keep the task on state even though we're landing on the fallback
        // phase — the in-place wizard there resumes THIS task's own
        // conversation instead of silently starting a stray new one.
        setTask(summary);
        setPhase('fallback');
        return;
      }

      if (summary.status === 'syncing') {
        setTask(summary);
        setPhase('alreadyPublished');
        return;
      }

      if (summary.status === 'completed' && retainedDraftCount(summary) === 0) {
        setTask(summary);
        setPhase('alreadyPublished');
        return;
      }

      // ready_for_review — fetch the full document (the list endpoint omits
      // suggestions/researchContent to keep the list light). The polling
      // effect already passes the full document through here, so skip the
      // refetch when suggestions are already present.
      const full = summary.suggestions ? summary : await getKnowledgeGeneratorTask(summary._id);
      const reviewableSuggestionTotal = [
        ...(full.suggestions?.objections || []),
        ...(full.suggestions?.websiteFaqs || []),
        ...(full.suggestions?.aiGeneratedFaqs || []),
      ].filter((item) => !item.synced && !item.removed).length;
      const additive = isAdditiveReviewTask(full);
      const nextProfile = additive ? null : buildCompanyProfileDraft(full.researchContent);
      const onboardingDraftTotal =
        unpublishedDraftItems(full.onboardingDraft?.landmines).length +
        unpublishedDraftItems(full.onboardingDraft?.competitors).length;
      const starterPlaybookTaskId = full.onboardingDraft?.starterPlaybookTaskId;
      // In the first-publication flow a linked draft is always reviewable.
      // In additive review it only counts if it's still unpublished — the
      // one extra lookup only fires here, never on the common non-additive
      // path — otherwise a task with nothing else left to review would be
      // treated as fully done and never reach this screen at all, stranding
      // an admin who deferred the playbook the first time around.
      const starterPlaybookAlreadySynced = additive && starterPlaybookTaskId
        ? (await getPlaybookIngestionTask(starterPlaybookTaskId).catch(() => null))?.status === 'synced'
        : false;
      const hasReviewableStarterPlaybook = Boolean(
        starterPlaybookTaskId && (!additive || !starterPlaybookAlreadySynced)
      );

      if (
        reviewableSuggestionTotal === 0 &&
        onboardingDraftTotal === 0 &&
        !nextProfile &&
        !hasReviewableStarterPlaybook
      ) {
        setTask(full);
        // A direct Review suggestions link must not silently become a setup
        // conversation when the last draft was published or removed between
        // loading the Overview and opening the task. A generic setup entry
        // can still use the wizard when no pre-filled draft exists.
        setPhase(taskId ? 'emptyReview' : 'fallback');
        return;
      }

      // A completed task with retained rows was produced by the old publish
      // completion rule. Preserve its persisted checks exactly while
      // reopening those rows; this is recovery, not first-time seeding.
      applyTask(full, summary.status !== 'completed');
      stopPolling();
      setPhase('review');
    },
    [applyTask, stopPolling, taskId]
  );

  // Initial load, in precedence order: an explicit task id (from a deep
  // link or a specific "Review draft" click) wins outright; otherwise
  // prefer the most recent superadmin-prepared initial draft, including the
  // legacy invite-time marker (this is the AI's starter draft for admins);
  // otherwise fall back to whatever draft the KB page's own readiness
  // already considers "active" (any provenance — spreadsheet import,
  // website rescan, an in-progress guided-setup task), the same task the
  // old `?setup=start` route used to resume the wizard against directly;
  // otherwise explicitly open a blank tenant draft before the fallback guide
  // mounts. The guide always receives the resulting task id.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadError(null);
        if (taskId) {
          const explicitTask = await getKnowledgeGeneratorTask(taskId);
          if (!active) return;
          await loadFromTaskSummary(explicitTask);
          return;
        }
        const resp = await getKnowledgeGeneratorTasks(1, 20);
        if (!active) return;
        const seeded = resp.data.find((t) => isSuperadminInitialDraft(t.createdBy));
        if (seeded) {
          await loadFromTaskSummary(seeded);
          return;
        }
        if (fallbackDraftTaskId) {
          const activeTask = await getKnowledgeGeneratorTask(fallbackDraftTaskId);
          if (!active) return;
          await loadFromTaskSummary(activeTask);
          return;
        }
        const openedDraft = await openKnowledgeGeneratorDraft();
        if (!active) return;
        await loadFromTaskSummary(openedDraft);
      } catch (err) {
        if (!active) return;
        console.error('[SeededOnboardingReview] Failed to load seeded task:', err);
        setLoadError(err instanceof Error ? err.message : 'The suggestions could not be loaded.');
        setPhase('loadError');
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, fallbackDraftTaskId, loadAttempt]);

  // Poll while the background researcher/generator is still running.
  useEffect(() => {
    if (phase !== 'preparing' || !task) {
      stopPolling();
      return;
    }
    pollingRef.current = setInterval(async () => {
      try {
        const full = await getKnowledgeGeneratorTask(task._id);
        await loadFromTaskSummary(full);
      } catch {
        // Transient — next tick retries. No user-facing error for a poll miss.
      }
    }, POLL_INTERVAL_MS);
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, task?._id]);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (!retryingStarterPlaybook || !task?._id) return;
    let active = true;
    const check = async () => {
      try {
        const full = await getKnowledgeGeneratorTask(task._id);
        if (!active) return;
        if (full.status === 'ready_for_review') {
          applyTask(full, false);
          setRetryingStarterPlaybook(false);
          setPlaybookMessage(full.onboardingDraft?.starterPlaybookTaskId
            ? 'Starter playbook is ready to review.'
            : full.onboardingDraft?.starterPlaybookError || 'Starter playbook retry finished without a draft.');
        }
      } catch (error) {
        if (active) setSelectionSaveError(error instanceof Error ? error.message : 'Could not check starter playbook generation.');
      }
    };
    void check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => { active = false; clearInterval(interval); };
  }, [applyTask, retryingStarterPlaybook, task?._id]);

  const retryStarterPlaybook = async () => {
    if (!task || retryingStarterPlaybook) return;
    setSelectionSaveError(null);
    setRetryingStarterPlaybook(true);
    try {
      const updated = await retryKnowledgeGeneratorStarterPlaybook(task._id);
      setTask(updated);
      setPlaybookMessage('Retrying only the missing starter playbook. Your existing edits are preserved.');
    } catch (error) {
      setRetryingStarterPlaybook(false);
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not retry starter playbook generation.');
    }
  };

  const hasPendingCleanup =
    objections.some((item) => item.styleCleanupStatus === 'pending') ||
    faqs.some((item) => item.styleCleanupStatus === 'pending') ||
    draftLandmines.some((item) => item.styleCleanupStatus === 'pending') ||
    draftCompetitors.some((item) => item.styleCleanupStatus === 'pending') ||
    Boolean(task?.researchContent?.styleCleanupPendingFields?.length);
  const activeTaskId = task?._id;

  // Cleanup is normally only a few seconds. Poll only during that window and
  // merge canonical wording by tempId; selections and manual edits remain
  // local until Publish. This avoids a second realtime protocol while keeping
  // the visible cards honest about what the server will publish.
  useEffect(() => {
    if (phase !== 'review' || !activeTaskId || !hasPendingCleanup) {
      if (cleanupPollingRef.current) clearInterval(cleanupPollingRef.current);
      cleanupPollingRef.current = null;
      return;
    }

    let active = true;
    let refreshing = false;
    cleanupPollingRef.current = setInterval(async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const full = await getKnowledgeGeneratorTask(activeTaskId);
        if (!active) return;
        const nextObjections = mergePolishedSuggestions(
          objections,
          full.suggestions?.objections,
          'objection',
        );
        const nextFaqs = mergePolishedSuggestions(
          mergePolishedSuggestions(faqs, full.suggestions?.websiteFaqs, 'websiteFaq'),
          full.suggestions?.aiGeneratedFaqs,
          'aiGeneratedFaq',
        );
        const landminesById = new Map((full.onboardingDraft?.landmines || []).map((item) => [item.tempId, item]));
        const competitorsById = new Map((full.onboardingDraft?.competitors || []).map((item) => [item.tempId, item]));
        const nextLandmines = draftLandmines.map((item) => {
          const latest = landminesById.get(item.tempId);
          return latest && !item.edited ? { ...item, ...latest, approved: item.approved } : item;
        });
        const nextCompetitors = draftCompetitors.map((item) => {
          const latest = competitorsById.get(item.tempId);
          return latest && !item.edited ? { ...item, ...latest, approved: item.approved } : item;
        });
        const canonicalProfile = buildCompanyProfileDraft(full.researchContent);
        const nextProfile = canonicalProfile
          ? PROFILE_FIELDS.reduce<CompanyProfileDraft>((profile, { key }) => {
              if (!profileEditedFields.has(key)) profile[key] = canonicalProfile[key] as never;
              return profile;
            }, { ...(companyProfile || canonicalProfile), include: companyProfile?.include ?? canonicalProfile.include })
          : companyProfile;
        const wasPristine = pristineSignatureRef.current === reviewSignature(
          objections,
          faqs,
          companyProfile,
          draftLandmines,
          draftCompetitors,
        );

        setTask(full);
        setObjections(nextObjections);
        setFaqs(nextFaqs);
        setCompanyProfile(nextProfile);
        setDraftLandmines(nextLandmines);
        setDraftCompetitors(nextCompetitors);
        if (wasPristine) {
          pristineSignatureRef.current = reviewSignature(
            nextObjections,
            nextFaqs,
            nextProfile,
            nextLandmines,
            nextCompetitors,
          );
        }
      } catch {
        // A missed refresh is harmless; Publish independently waits and reads
        // the canonical task before promoting anything.
      } finally {
        refreshing = false;
      }
    }, CLEANUP_POLL_INTERVAL_MS);

    return () => {
      active = false;
      if (cleanupPollingRef.current) clearInterval(cleanupPollingRef.current);
      cleanupPollingRef.current = null;
    };
  }, [
    phase,
    activeTaskId,
    hasPendingCleanup,
    objections,
    faqs,
    companyProfile,
    profileEditedFields,
    draftLandmines,
    draftCompetitors,
  ]);

  // One-time real counts for the Readiness panel's Landmines/Competitors
  // tiles — these have no seeded draft to read from, so "not configured
  // yet" only becomes accurate once we've actually asked. `pending` counts
  // items still gated from live detection (approvalStatus/isActive), same
  // meaning as "selected" for objections/FAQs above.
  useEffect(() => {
    if (phase !== 'review') return;
    let active = true;
    (async () => {
      try {
        const [landmines, competitors] = await Promise.all([
          getLandmines(1, 100).catch(() => ({ landmines: [] as LandmineItem[] })),
          getCompetitors(1, 100).catch(() => ({ competitors: [] as CompetitorItem[] })),
        ]);
        if (!active) return;
        setLandmineCounts({
          total: landmines.landmines.length,
          pending: landmines.landmines.filter((l) => l.approvalStatus === 'pending').length,
        });
        setCompetitorCounts({
          total: competitors.competitors.length,
          pending: competitors.competitors.filter((c) => !c.isActive).length,
        });
      } catch (err) {
        console.error('[SeededOnboardingReview] Failed to load landmine/competitor counts:', err);
      }
    })();
    return () => {
      active = false;
    };
  }, [phase]);

  // Two draft items that duplicate EACH OTHER never show up in the
  // publish-time duplicate check (which only compares against the already
  // published KB), so this runs independently on load — the reviewer needs
  // to see the conflict before deciding what to select, not after.
  useEffect(() => {
    if (phase !== 'review' || !task?._id) return undefined;
    let active = true;
    getKnowledgeGeneratorDraftDuplicates(task._id)
      .then((pairs) => {
        if (!active) return;
        setDraftDuplicates(pairs);
        setAcknowledgedDuplicatePairs(new Set(
          pairs.filter((pair) => pair.keptBoth)
            .map((pair) => duplicatePairKey(pair.a.tempId, pair.b.tempId))
        ));
      })
      .catch((err) => console.error('[SeededOnboardingReview] Failed to load draft duplicates:', err));
    return () => { active = false; };
  }, [phase, task?._id]);

  const duplicatePairKey = (tempIdA: string, tempIdB: string) => [tempIdA, tempIdB].sort().join('::');
  const activeSuggestionTempIds = new Set([...objections, ...faqs].map((item) => item.tempId));
  // Keep the server result intact while a removed item remains undoable. The
  // pair disappears from the review as soon as either side is removed, then
  // reappears automatically if Undo restores that side.
  const visibleDraftDuplicates = draftDuplicates.filter((pair) => (
    activeSuggestionTempIds.has(pair.a.tempId) && activeSuggestionTempIds.has(pair.b.tempId)
  ));
  const duplicatePairsByTempId = new Map<string, DraftDuplicatePair[]>();
  visibleDraftDuplicates.forEach((pair) => {
    [pair.a.tempId, pair.b.tempId].forEach((tempId) => {
      const pairs = duplicatePairsByTempId.get(tempId) || [];
      pairs.push(pair);
      duplicatePairsByTempId.set(tempId, pairs);
    });
  });
  const mergeableObjectionTempIds = new Set(
    objections.filter((item) => !item.synced && !item.pending).map((item) => item.tempId),
  );
  /** Duplicates where the admin has both items currently selected and has
   *  not yet acknowledged keeping both — the case that actually needs a
   *  decision, as opposed to a duplicate where one side is already
   *  deselected or removed. */
  const blockingDuplicatePairs = visibleDraftDuplicates.filter((pair) => {
    const key = duplicatePairKey(pair.a.tempId, pair.b.tempId);
    if (acknowledgedDuplicatePairs.has(key)) return false;
    const isSelected = (tempId: string) => [...objections, ...faqs]
      .some((item) => item.tempId === tempId && item.approved && !item.synced);
    return isSelected(pair.a.tempId) && isSelected(pair.b.tempId);
  });
  const duplicateTempIds = new Set(visibleDraftDuplicates.flatMap((pair) => [pair.a.tempId, pair.b.tempId]));
  const acknowledgeDuplicatePair = async (pair: DraftDuplicatePair) => {
    if (!task) return;
    const key = duplicatePairKey(pair.a.tempId, pair.b.tempId);
    if (savingDuplicatePairKeys.has(key)) return;
    setSavingDuplicatePairKeys((keys) => new Set(keys).add(key));
    setSelectionSaveError(null);
    try {
      await keepBothKnowledgeGeneratorDraftDuplicates(task._id, pair.version);
      setAcknowledgedDuplicatePairs((prev) => new Set(prev).add(key));
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not save the duplicate review.');
    } finally {
      setSavingDuplicatePairKeys((keys) => {
        const next = new Set(keys);
        next.delete(key);
        return next;
      });
    }
  };

  const openVariationMerge = (sourceTempId: string, initialTargetTempId?: string) => {
    const source = objections.find((objection) => objection.tempId === sourceTempId);
    const candidates = objections.filter((objection) => (
      objection.tempId !== sourceTempId && !objection.synced && !objection.pending
    ));
    if (!source || source.synced || source.pending || candidates.length === 0) {
      setSelectionSaveError('No other draft objection recommendation is available. Refresh and try again.');
      return;
    }
    setVariationMergeError(null);
    setVariationMergeRequest({ sourceTempId, source, initialTargetTempId });
  };

  const handleVariationMerge = async (targetTempId: string) => {
    if (!task || !variationMergeRequest || isMergingVariation) return;
    setIsMergingVariation(true);
    setVariationMergeError(null);
    try {
      const updatedTask = await mergeKnowledgeGeneratorObjectionVariation(
        task._id,
        variationMergeRequest.sourceTempId,
        targetTempId,
      );
      setTask(updatedTask);
      setObjections(
        toReviewList(updatedTask.suggestions?.objections, 'objection')
          .filter((suggestion) => !suggestion.synced && !suggestion.removed),
      );
      setDraftDuplicates((pairs) => pairs.filter((pair) => (
        pair.a.tempId !== variationMergeRequest.sourceTempId &&
        pair.b.tempId !== variationMergeRequest.sourceTempId
      )));
      setAcknowledgedDuplicatePairs((keys) => new Set(
        [...keys].filter((key) => !key.split('::').includes(variationMergeRequest.sourceTempId)),
      ));
      setVariationMergeRequest(null);
    } catch (error) {
      setVariationMergeError(error instanceof Error ? error.message : 'Could not add this variation. Please try again.');
    } finally {
      setIsMergingVariation(false);
    }
  };

  const toggleApproved = async (kind: SuggestionKind, tempId: string) => {
    if (!task) return;
    const list = kind === 'objection' ? objections : faqs;
    const current = list.find((item) => item.kind === kind && item.tempId === tempId);
    if (!current || current.synced) return;
    const approved = !current.approved;
    const mutation = (selectionMutationRef.current.get(tempId) || 0) + 1;
    selectionMutationRef.current.set(tempId, mutation);
    setSelectionSaveError(null);
    setSelectionRetry(null);
    setSavingSelectionKeys((keys) => new Set(keys).add(tempId));
    const updater = (list: ReviewSuggestion[]): ReviewSuggestion[] =>
      list.map((s) => (s.kind === kind && s.tempId === tempId && !s.synced ? { ...s, approved } : s));
    if (kind === 'objection') setObjections(updater);
    else setFaqs(updater);
    try {
      await updateKnowledgeGeneratorSuggestion(task._id, tempId, kind, { approved });
    } catch (error) {
      // Do not let an older failed request undo a later confirmed click.
      if (selectionMutationRef.current.get(tempId) === mutation) {
        const rollback = (items: ReviewSuggestion[]) => items.map((item) => (
          item.kind === kind && item.tempId === tempId ? { ...item, approved: current.approved } : item
        ));
        if (kind === 'objection') setObjections(rollback);
        else setFaqs(rollback);
        setSelectionSaveError(error instanceof Error ? error.message : 'Could not save that selection. Please try again.');
        setSelectionRetry(() => () => { void toggleApproved(kind, tempId); });
      }
    } finally {
      if (selectionMutationRef.current.get(tempId) === mutation) {
        setSavingSelectionKeys((keys) => { const next = new Set(keys); next.delete(tempId); return next; });
      }
    }
  };

  const toggleOnboardingApproved = async (type: 'landmine' | 'competitor', tempId: string) => {
    if ((type === 'landmine' && reviewingLandmineId === 'set')
      || (type === 'competitor' && reviewingCompetitorId === 'set')) return;
    const current = type === 'landmine'
      ? draftLandmines.find((item) => item.tempId === tempId)
      : draftCompetitors.find((item) => item.tempId === tempId);
    if (!current || current.synced || !task) return;
    const approved = !current.approved;
    const key = `${type}:${tempId}`;
    const mutation = (selectionMutationRef.current.get(key) || 0) + 1;
    selectionMutationRef.current.set(key, mutation);
    setSelectionSaveError(null);
    setSelectionRetry(null);
    setSavingSelectionKeys((keys) => new Set(keys).add(key));
    if (type === 'landmine') {
      setDraftLandmines((items) => items.map((item) => (
        item.tempId === tempId && !item.synced ? { ...item, approved } : item
      )));
    } else {
      setDraftCompetitors((items) => items.map((item) => (
        item.tempId === tempId && !item.synced ? { ...item, approved } : item
      )));
    }
    try {
      await persistKnowledgeGeneratorOnboardingSelection(task._id, type, tempId, approved);
    } catch (error) {
      if (selectionMutationRef.current.get(key) === mutation) {
        if (type === 'landmine') {
          setDraftLandmines((items) => items.map((item) => (
            item.tempId === tempId ? { ...item, approved: current.approved } : item
          )));
        } else {
          setDraftCompetitors((items) => items.map((item) => (
            item.tempId === tempId ? { ...item, approved: current.approved } : item
          )));
        }
        setSelectionSaveError(error instanceof Error ? error.message : 'Could not save that selection. Please try again.');
        setSelectionRetry(() => () => { void toggleOnboardingApproved(type, tempId); });
      }
    } finally {
      if (selectionMutationRef.current.get(key) === mutation) {
        setSavingSelectionKeys((keys) => { const next = new Set(keys); next.delete(key); return next; });
      }
    }
  };

  const reviewLandmineSet = async (reviewed: boolean) => {
    if (!task || [...savingSelectionKeys].some((key) => key.startsWith('landmine:'))) return;
    const selected = draftLandmines.filter((draft) => draft.approved && !draft.synced);
    if (!selected.length) return;
    setReviewingLandmineId('set');
    setSelectionSaveError(null);
    try {
      const updated = await reviewKnowledgeGeneratorLandmine(
        task._id,
        selected.map((draft) => draft.tempId),
        landmineSetVersion(selected),
        reviewed
      );
      setTask(updated);
      setDraftLandmines(unpublishedDraftItems(updated.onboardingDraft?.landmines));
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not record the landmine review.');
    } finally {
      setReviewingLandmineId(null);
    }
  };

  const reviewCompetitorSet = async (reviewed: boolean) => {
    if (!task || [...savingSelectionKeys].some((key) => key.startsWith('competitor:'))) return;
    const selected = draftCompetitors.filter((draft) => draft.approved && !draft.synced);
    if (!selected.length) return;
    setReviewingCompetitorId('set');
    setSelectionSaveError(null);
    try {
      const updated = await reviewKnowledgeGeneratorCompetitor(
        task._id,
        selected.map((draft) => draft.tempId),
        competitorSetVersion(selected),
        reviewed
      );
      setTask(updated);
      setDraftCompetitors(unpublishedDraftItems(updated.onboardingDraft?.competitors));
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not record the competitor review.');
    } finally {
      setReviewingCompetitorId(null);
    }
  };

  const startLandmineEdit = (draft: KnowledgeGeneratorLandmineDraft) => {
    setEditingLandmineId(draft.tempId);
    setLandmineEdit({
      trigger: draft.trigger,
      canSay: draft.canSay.map((item) => item.point).join('\n'),
      cannotSay: draft.cannotSay.map((item) => item.point).join('\n'),
      variations: (draft.triggerVariations || []).join('\n'),
    });
  };

  const saveLandmineEdit = async () => {
    if (!task || !editingLandmineId || savingLandmineRef.current) return;
    const original = draftLandmines.find((draft) => draft.tempId === editingLandmineId);
    if (!original) return;
    savingLandmineRef.current = true;
    setSavingLandmine(true);
    try {
      const updated = await updateKnowledgeGeneratorLandmineDraft(task._id, editingLandmineId, {
        trigger: landmineEdit.trigger,
        triggerVariations: landmineEdit.variations.split('\n').map((item) => item.trim()).filter(Boolean),
        canSay: mergeLandmineEditedPoints(
          landmineEdit.canSay,
          original.canSay || [],
          (point) => ({ point }),
        ),
        cannotSay: mergeLandmineEditedPoints(
          landmineEdit.cannotSay,
          original.cannotSay || [],
          (point) => ({ point, severity: 'general' }),
        ),
      }, landmineDraftVersion(original));
      setTask(updated);
      setDraftLandmines(unpublishedDraftItems(updated.onboardingDraft?.landmines));
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      setEditingLandmineId(null);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not save the landmine.');
    } finally {
      savingLandmineRef.current = false;
      setSavingLandmine(false);
    }
  };

  const addGeneratedVariations = async () => {
    if (!landmineEdit.trigger.trim()) return;
    try {
      const generated = await generateLandmineVariations(landmineEdit.trigger.trim());
      const merged = mergeLandmineVariations(landmineEdit.variations.split('\n'), generated);
      setLandmineEdit((current) => ({ ...current, variations: merged.join('\n') }));
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not generate variations.');
    }
  };

  /**
   * Removal is offered with an undo rather than a confirmation prompt: the
   * admin is pruning a list of thirty drafts, and a modal on every row would
   * make that miserable. Undo also covers the misclick a prompt is meant to
   * catch, without charging every deliberate removal for it.
   *
   * The removed item is kept, with the index it came from, so undo restores
   * it in place instead of appending it to the end of the section.
   */
  const removeSuggestion = async (kind: SuggestionKind, tempId: string) => {
    if (!task) return;
    const list = kind === 'objection' ? objections : faqs;
    const index = list.findIndex((s) => s.kind === kind && s.tempId === tempId);
    if (index === -1) return;
    const item = list[index];
    const setList = kind === 'objection' ? setObjections : setFaqs;
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, kind, tempId, true);
      setList((prev) => prev.filter((s) => !(s.kind === kind && s.tempId === tempId)));
      setRemovedSuggestions((prev) => [{ section: sectionOfKind(kind), item, index }, ...prev]);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not remove the draft.');
    }
  };

  const undoRemoveSuggestion = async (entry: RemovedSuggestion) => {
    if (!task) return;
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, entry.item.kind, entry.item.tempId, false);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not restore the draft.');
      return;
    }
    const setList = entry.item.kind === 'objection' ? setObjections : setFaqs;
    setList((prev) => {
      // Guard against a double-undo, and against the index having drifted
      // past the end of a list that shrank further after the removal.
      if (prev.some((s) => s.kind === entry.item.kind && s.tempId === entry.item.tempId)) return prev;
      const restored = [...prev];
      restored.splice(Math.min(entry.index, restored.length), 0, entry.item);
      return restored;
    });
    setRemovedSuggestions((prev) => prev.filter((e) => e !== entry));
  };

  const removeLandmine = async (tempId: string) => {
    if (!task) return;
    const index = draftLandmines.findIndex((l) => l.tempId === tempId);
    if (index === -1) return;
    const item = draftLandmines[index];
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, 'landmine', tempId, true);
      setDraftLandmines((prev) => prev.filter((l) => l.tempId !== tempId));
      setRemovedLandmines((prev) => [{ item, index }, ...prev]);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not remove the landmine.');
    }
  };

  const undoRemoveLandmine = async (entry: RemovedDraftItem<KnowledgeGeneratorLandmineDraft>) => {
    if (!task) return;
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, 'landmine', entry.item.tempId, false);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not restore the landmine.');
      return;
    }
    setDraftLandmines((prev) => {
      if (prev.some((l) => l.tempId === entry.item.tempId)) return prev;
      const restored = [...prev];
      restored.splice(Math.min(entry.index, restored.length), 0, entry.item);
      return restored;
    });
    setRemovedLandmines((prev) => prev.filter((e) => e !== entry));
  };

  const removeCompetitor = async (tempId: string) => {
    if (!task) return;
    const index = draftCompetitors.findIndex((c) => c.tempId === tempId);
    if (index === -1) return;
    const item = draftCompetitors[index];
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, 'competitor', tempId, true);
      setDraftCompetitors((prev) => prev.filter((c) => c.tempId !== tempId));
      setRemovedCompetitors((prev) => [{ item, index }, ...prev]);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not remove the competitor.');
    }
  };

  const undoRemoveCompetitor = async (entry: RemovedDraftItem<KnowledgeGeneratorCompetitorDraft>) => {
    if (!task) return;
    try {
      await persistKnowledgeGeneratorDraftRemoval(task._id, 'competitor', entry.item.tempId, false);
    } catch (error) {
      setSelectionSaveError(error instanceof Error ? error.message : 'Could not restore the competitor.');
      return;
    }
    setDraftCompetitors((prev) => {
      if (prev.some((c) => c.tempId === entry.item.tempId)) return prev;
      const restored = [...prev];
      restored.splice(Math.min(entry.index, restored.length), 0, entry.item);
      return restored;
    });
    setRemovedCompetitors((prev) => prev.filter((e) => e !== entry));
  };

  const toggleLandmineVariations = (tempId: string) => {
    setExpandedLandmineVariations((prev) => {
      const next = new Set(prev);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const startEdit = (item: ReviewSuggestion) => {
    setEditGenerationError(null);
    setEditing({ kind: item.kind, tempId: item.tempId });
    setEditForm({
      question: item.question,
      answer: item.answer,
      category: item.category || TopicCategory.OTHER,
      variations: item.variations.map((variation) => variation.question).join('\n'),
    });
  };

  /** Both halves of an answer are load-bearing: the question is what gets
   *  matched against what a prospect says, the answer is what the rep is
   *  told to say. An entry missing either is indexed but useless, so the
   *  edit dialog refuses to produce one rather than letting it reach the
   *  live dictionary through a publish. */
  const editIsSaveable = !!editForm.question.trim() && !!editForm.answer.trim();

  const generateEditVariations = async () => {
    if (!task || !editing || editing.kind === 'objection' || !editIsSaveable || generatingEditVariations) return;
    setGeneratingEditVariations(true);
    setEditGenerationError(null);
    try {
      const generated = await generateKnowledgeGeneratorSuggestionVariations(
        task._id,
        editing.tempId,
        editing.kind,
        editForm.question.trim(),
        editForm.answer.trim(),
        3,
      );
      setEditForm((current) => ({
        ...current,
        variations: mergeLandmineVariations(current.variations.split(/\r?\n/), generated).join('\n'),
      }));
    } catch (error) {
      setEditGenerationError(error instanceof Error ? error.message : 'Could not generate FAQ variations.');
    } finally {
      setGeneratingEditVariations(false);
    }
  };

  // Escape closes the edit dialog. Keyboard users had no way out of it at
  // all: it isn't dismissible by any key, and tabbing past Cancel walks
  // straight back into the review list behind it.
  useEffect(() => {
    if (!editing) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditing(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editing]);

  const saveEdit = async () => {
    if (!editing || !editIsSaveable || !task || savingEditRef.current) return;
    savingEditRef.current = true;
    const question = editForm.question.trim();
    const answer = editForm.answer.trim();
    const currentSuggestion = (editing.kind === 'objection' ? objections : faqs)
      .find((suggestion) => suggestion.kind === editing.kind && suggestion.tempId === editing.tempId);
    const existingVariations = currentSuggestion?.variations || [];
    const usedVariationIds = new Set<string>();
    const newVariationIdSeed = Date.now();
    const variations = editForm.variations
      .split(/\r?\n/)
      .map((variation) => variation.trim())
      .filter(Boolean)
      .map((variationQuestion, index) => {
        // Preserve an existing identity when a line is unchanged or edited in
        // place. Newly-added lines receive a task-local stable ID before the
        // whole array is persisted through the existing suggestion endpoint.
        const exactMatch = existingVariations.find((variation) => (
          !usedVariationIds.has(variation.tempId) && variation.question === variationQuestion
        ));
        const positionalMatch = existingVariations[index]
          && !usedVariationIds.has(existingVariations[index].tempId)
          ? existingVariations[index]
          : null;
        const existing = exactMatch || positionalMatch;
        const tempId = existing?.tempId
          || `${editing.tempId}:manual-variation:${newVariationIdSeed}:${index}`;
        usedVariationIds.add(tempId);
        return { tempId, question: variationQuestion };
      });
    const updater = (list: ReviewSuggestion[]) =>
      list.map((s) =>
        s.kind === editing.kind && s.tempId === editing.tempId
          ? { ...s, question, answer, category: editForm.category, variations, approved: true, edited: true, styleCleanupStatus: 'manual' as const, styleCleanedAt: null }
          : s
      );
    try {
      await updateKnowledgeGeneratorSuggestion(task._id, editing.tempId, editing.kind, {
        question,
        answer,
        category: editForm.category,
        variations,
        approved: true,
        edited: true,
        styleCleanupStatus: 'manual',
        styleCleanedAt: null,
      });
      if (editing.kind === 'objection') setObjections(updater);
      else setFaqs(updater);
      setEditing(null);
    } catch (error) {
      setPublishError(error instanceof Error ? error.message : 'Could not save this draft edit.');
    } finally {
      savingEditRef.current = false;
    }
  };

  const applySuggestionUpdate = async (kind: SuggestionKind, tempId: string, question: string, answer: string) => {
    const updater = (list: ReviewSuggestion[]): ReviewSuggestion[] =>
      list.map((s) => (
        s.kind === kind && s.tempId === tempId
          ? { ...s, question, answer, edited: true, styleCleanupStatus: 'manual' as const, styleCleanedAt: null }
          : s
      ));
    if (!task) throw new Error('The current draft is unavailable.');
    await updateKnowledgeGeneratorSuggestion(task._id, tempId, kind, {
      question,
      answer,
      edited: true,
      styleCleanupStatus: 'manual',
      styleCleanedAt: null,
    });
    if (kind === 'objection') setObjections(updater);
    else setFaqs(updater);
  };

  /** Sends a correction objective to the already-mounted smart wizard. */
  const openImproveSuggestion = (item: ReviewSuggestion) => {
    const section: AssistantSection = item.kind === 'objection' ? 'objections' : 'faqs';
    setActiveGuideTarget({ type: 'suggestion', kind: item.kind, tempId: item.tempId });
    setWizardRequest({
      id: ++wizardRequestId.current,
      kind: 'improve',
      stage: section,
      contextLabel: item.kind === 'objection' ? 'Objection' : 'FAQ',
      current: { question: item.question, answer: item.answer },
      onApply: async (updated) => {
        if ('question' in updated) {
          await applySuggestionUpdate(item.kind, item.tempId, updated.question, updated.answer);
          setActiveGuideTarget(null);
        }
      },
    });
  };

  const openImproveProfileField = (field: ProfileFieldKey, label: string) => {
    if (!companyProfile) return;
    const raw = companyProfile[field];
    const current: ImproveItemCurrent = Array.isArray(raw) ? { value: raw } : { value: raw };
    setActiveGuideTarget({ type: 'profile', field });
    setWizardRequest({
      id: ++wizardRequestId.current,
      kind: 'improve',
      stage: 'company_context',
      contextLabel: label,
      current,
      onApply: async (updated) => {
        if ('value' in updated) {
          const expectsList = Array.isArray(raw);
          if (expectsList !== Array.isArray(updated.value)) {
            throw new Error(`The suggested ${label.toLowerCase()} has the wrong format.`);
          }
          if (!task) throw new Error('The current draft is unavailable.');
          await updateKnowledgeGeneratorCompanyProfile(task._id, field, updated.value);
          setCompanyProfile((prev) => (prev ? {
            ...prev,
            [field]: updated.value,
            ...(field === 'certifications' ? { claimsReviewedAt: null, claimsReviewedByEmail: null, claimsReviewedVersion: null } : {}),
          } : prev));
          setProfileEditedFields((prev) => new Set(prev).add(field));
          setActiveGuideTarget(null);
        }
      },
    });
  };

  const selectedDraftPreviewItems: DraftPreviewItem[] = [...objections, ...faqs]
    .filter((item) => item.approved && !item.synced)
    .map(toDraftPreviewItem);
  const selectedObjectionsCount = objections.filter((o) => o.approved && !o.synced).length;
  const selectedFaqsCount = faqs.filter((f) => f.approved && !f.synced).length;
  const selectedLandminesCount = draftLandmines.filter((l) => l.approved && !l.synced).length;
  const selectedLandmineDrafts = draftLandmines.filter((l) => l.approved && !l.synced);
  const landmineSetReviewed = Boolean(
    selectedLandmineDrafts.length &&
    task?.onboardingDraft?.landmineSetReview?.reviewedAt &&
    task?.onboardingDraft?.landmineSetReview?.version === landmineSetVersion(selectedLandmineDrafts)
  );
  const selectedCompetitorsCount = draftCompetitors.filter((c) => c.approved && !c.synced).length;
  const selectedCompetitorDrafts = draftCompetitors.filter((c) => c.approved && !c.synced);
  // True while the linked playbook is still an open decision — unpublished.
  // Once it's synced, `includeStarterPlaybook` staying `true` from an
  // earlier round shouldn't make the UI claim there's still something to
  // decide or publish for it.
  const starterPlaybookOutstanding = Boolean(
    task?.onboardingDraft?.starterPlaybookTaskId && starterPlaybook?.status !== 'synced'
  );
  // Playbook inclusion is decided the same way whether this is the first
  // publish or an additive one: the top-level checkbox drives it, and it
  // rides along in the same outer publish action as everything else — there
  // is no separate standalone publish step during additive review.
  const starterPlaybookSelected = Boolean(starterPlaybookOutstanding && includeStarterPlaybook);
  // Mirrors the competitors/landmines pattern: a linked playbook that hasn't
  // published yet stays reviewable in additive review too, instead of
  // disappearing the moment unrelated content on the task goes live.
  const starterPlaybookNeedsReview = starterPlaybookOutstanding;
  const selectedPlaybookAudienceCount = starterPlaybook?.draftRoles.filter((role) => role.isActive !== false).length || 0;
  const starterPlaybookCleanupReady = Boolean(
    starterPlaybook?.mode === 'manual' ||
    (starterPlaybook?.styleCleanupStatus === 'cleaned' && starterPlaybook.styleCleanedAt)
  );
  const starterPlaybookProtectedReady = Boolean(
    starterPlaybook?.mode !== 'upload' ||
    (starterPlaybook.protectedContentStatus === 'verified' && starterPlaybook.protectedContentVerifiedAt)
  );
  const starterPlaybookHasUnresolvedContent = Boolean(
    starterPlaybookSelected &&
    starterPlaybook?.status !== 'synced' &&
    starterPlaybook &&
    hasUnresolvedUncertaintyLabel({
      draftPlaybookName: starterPlaybook.draftPlaybookName,
      draftPlaybookDisplayName: starterPlaybook.draftPlaybookDisplayName,
      draftRoles: starterPlaybook.draftRoles,
    })
  );
  const starterPlaybookReady = Boolean(
    !starterPlaybookSelected ||
    (starterPlaybook && (
      starterPlaybook.status === 'synced' ||
      (
        starterPlaybook.status === 'ready_for_review' &&
        starterPlaybookCleanupReady &&
        starterPlaybookProtectedReady &&
        !starterPlaybookHasUnresolvedContent &&
        selectedPlaybookAudienceCount > 0
      )
    ))
  );
  const unresolvedSelectedItems = [
    ...objections.filter((item) => item.approved && !item.synced),
    ...faqs.filter((item) => item.approved && !item.synced),
    ...selectedLandmineDrafts,
    ...selectedCompetitorDrafts,
    ...(companyProfile?.include ? [companyProfile] : []),
    ...(starterPlaybookHasUnresolvedContent && starterPlaybook ? [starterPlaybook] : []),
  ].filter((item) => hasUnresolvedUncertaintyLabel(item));
  const unresolvedSelectedCount = unresolvedSelectedItems.length;
  const unresolvedSelectedResponses = [
    ...objections.filter((item) => item.approved && !item.synced),
    ...faqs.filter((item) => item.approved && !item.synced),
  ].filter((item) => hasUnresolvedUncertaintyLabel(item));
  const competitorSetReviewed = Boolean(
    selectedCompetitorDrafts.length &&
    task?.onboardingDraft?.competitorSetReview?.reviewedAt &&
    task?.onboardingDraft?.competitorSetReview?.version === competitorSetVersion(selectedCompetitorDrafts)
  );

  const canPublish =
    !publishing &&
    unresolvedSelectedCount === 0 &&
    !(companyProfile?.certifications.length && !companyProfile.claimsReviewedAt) &&
    blockingDuplicatePairs.length === 0 &&
    // Mirrors backend's own LANDMINE_REVIEW_REQUIRED / COMPETITOR_REVIEW_
    // REQUIRED gates (knowledgeGenerator.js's /publish-all): review is only
    // required when there's a selected, unsynced draft of that kind to
    // review — `landmineSetReviewed`/`competitorSetReviewed` are already
    // `false` when nothing is selected, so this checks "not selected OR
    // reviewed", not the review flag on its own.
    (selectedLandminesCount === 0 || landmineSetReviewed) &&
    (selectedCompetitorsCount === 0 || competitorSetReviewed) &&
    starterPlaybookReady &&
    (
      selectedObjectionsCount > 0 ||
      selectedFaqsCount > 0 ||
      selectedLandminesCount > 0 ||
      selectedCompetitorsCount > 0 ||
      starterPlaybookSelected ||
      !!companyProfile?.include
    );

  const handlePublish = async () => {
    if (!task || !canPublish || publishingRef.current) return;
    publishingRef.current = true;
    setPublishing(true);
    setPublishError(null);
    setPublishWarnings([]);
    try {
      const objectionTempIds = objections.filter((o) => o.approved && !o.synced).map((o) => o.tempId);
      const websiteFaqTempIds = faqs
        .filter((f) => f.kind === 'websiteFaq' && f.approved && !f.synced)
        .map((f) => f.tempId);
      const aiGeneratedFaqTempIds = faqs
        .filter((f) => f.kind === 'aiGeneratedFaq' && f.approved && !f.synced)
        .map((f) => f.tempId);
      const landmineTempIds = draftLandmines.filter((l) => l.approved && !l.synced).map((l) => l.tempId);
      const competitorTempIds = draftCompetitors.filter((c) => c.approved && !c.synced).map((c) => c.tempId);
      const removedSuggestionTempIds = removedSuggestions.map(({ item }) => item.tempId);

      const results = await publishKnowledgeGeneratorTask(task._id, {
        suggestions: {
          objections: objections.filter((item) => item.edited && isPublishableSuggestion(item)).map(stripReviewFields),
          websiteFaqs: faqs.filter((f) => f.kind === 'websiteFaq' && f.edited && isPublishableSuggestion(f)).map(stripReviewFields),
          aiGeneratedFaqs: faqs.filter((f) => f.kind === 'aiGeneratedFaq' && f.edited && isPublishableSuggestion(f)).map(stripReviewFields),
        },
        companyProfile: companyProfile ? {
          include: companyProfile.include,
          whatYouSell: companyProfile.whatYouSell,
          targetAudience: companyProfile.targetAudience,
          howItWorks: companyProfile.howItWorks,
          certifications: companyProfile.certifications,
          differentiators: companyProfile.differentiators,
          editedFields: [...profileEditedFields],
        } : undefined,
        objectionTempIds,
        websiteFaqTempIds,
        aiGeneratedFaqTempIds,
        landmineTempIds,
        competitorTempIds,
        includeStarterPlaybook: starterPlaybookSelected,
        removedSuggestionTempIds,
      });

      // Counts come from the server's own tally, not from what we asked it
      // to publish — on a partial publish those differ, and claiming the
      // requested number would overstate what actually went live.
      const published = {
        objections: results.totalObjections || 0,
        faqs: results.totalFaqs || 0,
        companyProfile: !!companyProfile?.include,
        landmines: results.totalLandmines || 0,
        competitors: results.totalCompetitors || 0,
      };
      setPublishWarnings((results.cleanupWarnings || []).map((warning) => warning.message));

      if (results.errors?.length) {
        // Partial publish: some items are live, some are not, and the task
        // now holds the authoritative synced flags. Re-read it (without
        // re-seeding the starter set, which would discard the admin's own
        // ticks) so the list shows what already published as published, and
        // a retry sends only the remainder.
        const refreshed = await getKnowledgeGeneratorTask(task._id).catch(() => null);
        if (refreshed) applyTask(refreshed, false);
        // The removals went with the save, so there is nothing left to undo.
        setRemovedSuggestions([]);
        setRemovedLandmines([]);
        setRemovedCompetitors([]);
        const failed = results.errors.length;
        const live = published.objections + published.faqs + published.landmines + published.competitors;
        setPublishError(
          live > 0
            ? `${live} item${live === 1 ? '' : 's'} published, but ${failed} could not be. The published items are live. Press Publish again to retry just the rest.`
            : `Nothing was published. ${failed} item${failed === 1 ? '' : 's'} failed. Nothing is live yet, so it is safe to try again.`,
        );
        return;
      }

      // Clearing the Actions Center entry is bookkeeping that happens after
      // the content is already live. Letting it throw used to turn a fully
      // successful publish into "Could not publish", sending the admin back
      // to press Publish on work that had already shipped.
      try {
        await actionsApi.completeActionsByType('kb_setup');
      } catch (actionErr) {
        console.warn('[SeededOnboardingReview] Published, but could not clear the kb_setup action:', actionErr);
      }

      setRemovedSuggestions([]);
      setRemovedLandmines([]);
      setRemovedCompetitors([]);
      setPublishedCounts(published);
      setRepRuntimeReadiness(null);
      // Keep the modal mounted briefly so a physical double-click cannot
      // land on a newly exposed control after the first click publishes.
      await new Promise((resolve) => window.setTimeout(resolve, 300));
      setPhase('completed');
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : 'Could not publish. Try again.');
    } finally {
      publishingRef.current = false;
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (!showPublishConfirmation || publishing) return;
    const dismissConfirmation = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowPublishConfirmation(false);
    };
    document.addEventListener('keydown', dismissConfirmation);
    return () => document.removeEventListener('keydown', dismissConfirmation);
  }, [publishing, showPublishConfirmation]);

  // Publishing writes Mongo first; reps search the independently rebuilt
  // FAISS artifact. Keep this phase honest until the backend confirms the
  // exact published Q&A text is part of that artifact.
  useEffect(() => {
    if (phase !== 'completed' || !task?._id) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const readiness = await getKnowledgeGeneratorRepRuntimeReadiness(task._id);
        if (cancelled) return;
        setRepRuntimeReadiness(readiness);
        if (readiness.status === 'ready' || readiness.status === 'not_applicable' || readiness.status === 'error') {
          if (runtimePollingRef.current) clearInterval(runtimePollingRef.current);
          runtimePollingRef.current = null;
        }
      } catch (error) {
        // The content is already published; a transient status read must not
        // rewrite that fact as a failed publish.
        console.warn('[SeededOnboardingReview] Could not read rep-runtime readiness:', error);
      }
    };
    void check();
    runtimePollingRef.current = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (runtimePollingRef.current) clearInterval(runtimePollingRef.current);
      runtimePollingRef.current = null;
    };
  }, [phase, task?._id]);

  // ---------------------------------------------------------------------
  // Render: non-review phases
  // ---------------------------------------------------------------------

  /** Generated drafts the starter-set publish left behind. Counted from the
   *  task rather than the review lists, which the already-published phase
   *  never populates — and preferring the list API's aggregated counts,
   *  since that endpoint deliberately projects the suggestion arrays away
   *  and returns sizes instead. */
  const draftedTotal =
    (task?.suggestedObjectionsCount ?? task?.suggestions?.objections?.length ?? 0) +
    (task?.suggestedFaqsCount ??
      (task?.suggestions?.websiteFaqs?.length ?? 0) + (task?.suggestions?.aiGeneratedFaqs?.length ?? 0));
  const publishedTotal = (task?.syncResults?.totalObjections ?? 0) + (task?.syncResults?.totalFaqs ?? 0);
  const unpublishedDraftCount = task?.syncResults?.remainingCount ?? Math.max(0, draftedTotal - publishedTotal);

  if (phase === 'loading') {
    return (
      <div className="h-full w-full bg-white flex items-center justify-center">
        <div className="text-center text-ink-muted text-sm">Loading your draft…</div>
      </div>
    );
  }

  if (phase === 'loadError') {
    return (
      <div className="h-full w-full bg-white flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <h2 className="heading-4 mb-2">Could not load review suggestions</h2>
          <p className="text-ink-secondary mb-6">{loadError || 'The suggestions could not be loaded.'}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setPhase('loading');
                setLoadAttempt((attempt) => attempt + 1);
              }}
              className="kb-button kb-button-primary px-5 py-2"
            >
              Try again
            </button>
            <button type="button" onClick={onExit} className="kb-button kb-button-tertiary px-4 py-2">
              Back to Knowledge Base
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'emptyReview') {
    return (
      <div className="h-full w-full bg-white flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <h2 className="heading-4 mb-2">No suggestions waiting for review</h2>
          <p className="text-ink-secondary mb-6">This draft no longer has unpublished suggestions.</p>
          <button type="button" onClick={onExit} className="kb-button kb-button-primary px-5 py-2">
            Back to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'fallback') {
    // No usable pre-filled draft (nothing seeded, nothing else active, or
    // what we found had no content yet) — drop straight into the guided
    // conversational wizard in place, rather than bouncing back out to a
    // separate route the way this used to hand off to `?setup=start`.
    // `task?._id`, when set (the 'error'/empty-content branches above both
    // keep it), resumes that same task's conversation instead of starting
    // a stray new one; unset, this is a true fresh start.
    return (
      <div className="h-full w-full bg-white">
        <OnboardingWizard onExit={onExit} draftTaskId={task?._id} />
      </div>
    );
  }

  if (phase === 'alreadyPublished') {
    return (
      <div className="h-full w-full bg-white flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <div className="text-4xl mb-4">✓</div>
          <h2 className="heading-4 mb-2">Your knowledge base is already set up</h2>
          <p className="text-ink-secondary mb-6">
            The starter content we drafted for you has already been published. You can review or add more
            from the Knowledge Base page any time.
          </p>
          {unpublishedDraftCount > 0 && (
            <p className="text-sm text-ink-secondary mb-6">
              {unpublishedDraftCount} more draft{unpublishedDraftCount === 1 ? '' : 's'} from that first pass{' '}
              {unpublishedDraftCount === 1 ? 'is' : 'are'} still unpublished.{' '}
              <a href="/knowledge-base?tab=overview&focus=imports" className="text-signature hover:text-signature-strong font-medium">
                Review them in Imports &amp; Drafts
              </a>
              .
            </p>
          )}
          <button onClick={onExit} className="kb-button kb-button-primary px-5 py-2">
            Go to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'preparing') {
    return (
      <div className="h-full w-full bg-white flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <div className="w-8 h-8 mx-auto mb-4 border-2 border-signature border-t-transparent rounded-full animate-spin" />
          <h2 className="heading-4 mb-2">We're preparing your draft</h2>
          <p className="text-ink-secondary mb-6">{preparingMessage}</p>
          <button onClick={onExit} className="px-4 py-2 text-sm text-ink-muted hover:text-ink-secondary">
            Go to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'completed') {
    const runtimeReady = repRuntimeReadiness?.status === 'ready';
    const runtimeNotApplicable = repRuntimeReadiness?.status === 'not_applicable';
    const runtimeFailed = repRuntimeReadiness?.status === 'error';
    return (
      <div className="h-full w-full bg-white overflow-y-auto">
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <div className="text-4xl mb-4">✓</div>
          <h2 className="heading-3 mb-2">
            {runtimeReady ? 'Your live-call assistant is ready' : 'Your starter set is published'}
          </h2>
          <p className="text-ink-secondary mb-8">
            {publishedCounts.objections} objection{publishedCounts.objections === 1 ? '' : 's'} and{' '}
            {publishedCounts.faqs} FAQ{publishedCounts.faqs === 1 ? '' : 's'} are now live
            {publishedCounts.landmines > 0 ? `, including ${publishedCounts.landmines} reviewed Landmine${publishedCounts.landmines === 1 ? '' : 's'}` : ''}
            {publishedCounts.competitors > 0 ? ` and ${publishedCounts.competitors} competitor${publishedCounts.competitors === 1 ? '' : 's'} added` : ''}
            {publishedCounts.companyProfile ? ', along with your company profile' : ''}.
            {runtimeReady
              ? ' These answers are now available on live calls.'
              : runtimeFailed
                ? ' The live-call index needs attention before these answers can be used by reps.'
                : runtimeNotApplicable
                  ? ' No objection or FAQ was included, so there is no live-call guidance to index yet.'
                : ` Live-call guidance is indexing${repRuntimeReadiness ? ` (${repRuntimeReadiness.ready}/${repRuntimeReadiness.expected} answers ready)` : ''}.`}
          </p>

          {(() => {
            const deps = repRuntimeReadiness?.dependencies;
            const landmineLag = deps?.landmines?.status === 'published_pending_cache';
            const competitorLag = deps?.competitors?.status === 'published_pending_cache';
            if (!landmineLag && !competitorLag) return null;
            const parts = [
              landmineLag ? `landmines (up to ${deps!.landmines.cacheRefreshIntervalSeconds}s)` : null,
              competitorLag ? `competitors (up to ${deps!.competitors.cacheRefreshIntervalSeconds}s)` : null,
            ].filter(Boolean).join(' and ');
            return (
              <p className="text-xs text-ink-muted mb-6 -mt-4">
                Saved to the knowledge base. Reps on an active call may briefly see the previous version of {parts} while the call assistant reloads its cache.
              </p>
            );
          })()}

          {publishWarnings.length > 0 && (
            <div className="text-left mb-6 rounded-xl border border-warning bg-warning-tint p-4 text-sm text-ink">
              {publishWarnings.length} item{publishWarnings.length === 1 ? '' : 's'} published with original wording because automatic cleanup did not complete.
            </div>
          )}

          <div className="text-left bg-surface-subtle border border-hairline rounded-xl p-5 mb-8">
            <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
              Worth doing next
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-ink-secondary">Review the rest of the generated suggestions</span>
                <a href="/knowledge-base" className="text-signature hover:text-signature-strong font-medium">
                  Open
                </a>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-secondary">Add and approve Landmines</span>
                <a href="/knowledge-base?tab=landmines" className="text-signature hover:text-signature-strong font-medium">
                  Open
                </a>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-secondary">Confirm your competitors</span>
                <a href="/knowledge-base?tab=competitors" className="text-signature hover:text-signature-strong font-medium">
                  Open
                </a>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-secondary">Create your first training track</span>
                <a href="/training/builder" className="text-signature hover:text-signature-strong font-medium">
                  Open
                </a>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-ink-secondary">Connect your calendar</span>
                <a href="/calendar" className="text-signature hover:text-signature-strong font-medium">
                  Open
                </a>
              </li>
            </ul>
          </div>

          <button onClick={onExit} className="kb-button kb-button-primary px-5 py-2">
            Go to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Render: review phase
  // ---------------------------------------------------------------------

  /** Review edits live in this component only — nothing is written until
   *  Publish — so closing without publishing discards them. Warn instead of
   *  losing them quietly. */
  const hasUnsavedReviewWork =
    pristineSignatureRef.current !== null &&
    reviewSignature(objections, faqs, companyProfile, draftLandmines, draftCompetitors) !== pristineSignatureRef.current;

  const handleClose = async () => {
    if (
      hasUnsavedReviewWork &&
      !(await confirm({
        title: 'Discard unpublished changes?',
        message: 'Your review changes have not been published yet, and closing will discard them.',
        confirmLabel: 'Discard and close',
        cancelLabel: 'Keep reviewing',
        variant: 'destructive',
      }))
    ) {
      return;
    }
    onExit();
  };

  const claimsToVerifyCount = companyProfile?.certifications.length || 0;
  const hasUnverifiedClaims = claimsToVerifyCount > 0 && !companyProfile?.claimsReviewedAt;
  const companyProfileNeedsClarification = Boolean(companyProfile?.include && hasUnresolvedUncertaintyLabel(companyProfile));
  const claimsCleanupPending = Boolean(task?.researchContent?.styleCleanupPendingFields?.includes('certifications'));

  const selectedResponseCount = selectedObjectionsCount + selectedFaqsCount;

  const atLeastOneItemSelected =
    selectedResponseCount > 0 || selectedLandminesCount > 0 || selectedCompetitorsCount > 0 ||
    starterPlaybookSelected || !!companyProfile?.include;
  const selectionParts = [
    selectedResponseCount > 0 ? `${selectedResponseCount} response${selectedResponseCount === 1 ? '' : 's'}` : '',
    selectedLandminesCount > 0 ? `${selectedLandminesCount} landmine${selectedLandminesCount === 1 ? '' : 's'}` : '',
    selectedCompetitorsCount > 0 ? `${selectedCompetitorsCount} competitor${selectedCompetitorsCount === 1 ? '' : 's'}` : '',
    starterPlaybookSelected ? 'starter playbook' : '',
    companyProfile?.include ? 'company profile' : '',
  ].filter(Boolean);
  const selectionSummary = selectionParts.join(', ');
  const publishBlockers: { key: string; detail: string; onClick: () => void }[] = [
    ...(unresolvedSelectedResponses.length > 0 ? [{
      key: 'unresolved-content',
      detail: `${unresolvedSelectedResponses.length} selected response${unresolvedSelectedResponses.length === 1 ? ' needs' : 's need'} clarification`,
      onClick: () => goToSixCardSection(unresolvedSelectedResponses[0].kind === 'objection' ? 'objections' : 'faqs'),
    }] : []),
    ...(companyProfileNeedsClarification || hasUnverifiedClaims ? [{
      key: 'company-profile-review',
      detail: companyProfileNeedsClarification && hasUnverifiedClaims
        ? 'Review the company profile’s clarifications and claims'
        : companyProfileNeedsClarification
        ? 'Resolve the company profile’s clarification notes'
        : `${claimsToVerifyCount} company claim${claimsToVerifyCount === 1 ? ' needs' : 's need'} verification`,
      onClick: () => goToSixCardSection('company_context'),
    }] : []),
    ...(blockingDuplicatePairs.length > 0 ? [{
      key: 'duplicates',
      detail: `${blockingDuplicatePairs.length} possible duplicate pair${blockingDuplicatePairs.length === 1 ? '' : 's'} must be reviewed`,
      onClick: () => goToSixCardSection(blockingDuplicatePairs[0].a.kind === 'objection' ? 'objections' : 'faqs'),
    }] : []),
    ...(selectedLandminesCount > 0 && (
      !landmineSetReviewed || selectedLandmineDrafts.some((item) => hasUnresolvedUncertaintyLabel(item))
    ) ? [{
      key: 'landmine-review', detail: 'Review the selected landmine guidance', onClick: () => goToSixCardSection('landmines'),
    }] : []),
    ...(selectedCompetitorsCount > 0 && (
      !competitorSetReviewed || selectedCompetitorDrafts.some((item) => hasUnresolvedUncertaintyLabel(item))
    ) ? [{
      key: 'competitor-review', detail: 'Review the selected competitor positioning', onClick: () => goToSixCardSection('competitors'),
    }] : []),
    ...(!starterPlaybookReady ? [{
      key: 'playbook-readiness',
      detail: !starterPlaybook
        ? 'The starter playbook is unavailable'
        : starterPlaybookHasUnresolvedContent
        ? 'Resolve the playbook’s inferred or clarification notes'
        : !starterPlaybookCleanupReady
        ? 'Wait for playbook cleanup to finish'
        : !starterPlaybookProtectedReady
        ? 'Verify the protected playbook wording'
        : 'Select at least one playbook audience',
      onClick: () => setShowPlaybookReview(true),
    }] : []),
  ];
  const blockingChecklistCount = publishBlockers.length;

  const handleMarkClaimsReviewed = async () => {
    if (!task || !companyProfile) return;
    setMarkingClaimsReviewed(true);
    setPublishError(null);
    try {
      const updatedTask = await markKnowledgeGeneratorClaimsReviewed(task._id, companyProfile.certifications);
      const claimsReviewedAt = updatedTask.researchContent?.claimsReviewedAt || new Date().toISOString();
      const claimsReviewedByEmail = updatedTask.researchContent?.claimsReviewedByEmail || null;
      const claimsReviewedVersion = updatedTask.researchContent?.claimsReviewedVersion || null;
      setTask(updatedTask);
      setCompanyProfile((prev) => (prev ? { ...prev, claimsReviewedAt, claimsReviewedByEmail, claimsReviewedVersion } : prev));
      // The review endpoint only succeeds when the submitted certifications
      // exactly match what is already saved server-side (it 409s otherwise)
      // — so a success here means there is no unsaved local edit left to
      // account for. Clear the dirty marker so publish reads this as
      // reviewed via the normal path instead of unconditionally rejecting
      // it as "certifications were edited, no matching review exists".
      setProfileEditedFields((prev) => {
        if (!prev.has('certifications')) return prev;
        const next = new Set(prev);
        next.delete('certifications');
        return next;
      });
    } catch (error) {
      setPublishError(error instanceof Error ? error.message : 'Could not mark company claims as reviewed.');
    } finally {
      setMarkingClaimsReviewed(false);
    }
  };

  /** One company-profile field, shown as read-only draft text by default with
   *  real nudges — Improve with guide / Edit manually — instead
   *  of always being an open text box. This is the shift from "here's an
   *  editable field, figure out what to do with it" to "here's a draft,
   *  choose how you want to handle it." */
  const renderProfileField = ({ key, label, emptyHint }: (typeof PROFILE_FIELDS)[number]) => {
    if (!companyProfile) return null;
    const raw = companyProfile[key];
    const isListField = Array.isArray(raw);
    const displayValue = isListField ? (raw as string[]).join(', ') : (raw as string);
    const isEmpty = isListField ? (raw as string[]).length === 0 : !(raw as string).trim();
    const writing = !!fieldWriting[key];
    const researchField = PROFILE_TO_RESEARCH_FIELD[key];
    const cleanupPending = task?.researchContent?.styleCleanupPendingFields?.includes(researchField);
    const polished = task?.researchContent?.styleCleanedFields?.includes(researchField);
    const cleanupFailed = task?.researchContent?.styleCleanupErrorFields?.includes(researchField);
    const needsClarification = hasUnresolvedUncertaintyLabel(raw);
    const improvingWithGuide = activeGuideTarget?.type === 'profile' && activeGuideTarget.field === key;

    const setValue = (next: string) => {
      const parsed: string | string[] = isListField
        ? next.split(',').map((s) => s.trim()).filter(Boolean)
        : next;
      setCompanyProfile((prev) => (prev ? {
        ...prev,
        [key]: parsed,
        ...(key === 'certifications' ? { claimsReviewedAt: null, claimsReviewedByEmail: null, claimsReviewedVersion: null } : {}),
      } : prev));
      setProfileEditedFields((prev) => new Set(prev).add(key));
    };

    return (
      <div
        key={key}
        role="group"
        aria-label={`Company profile field: ${label}`}
        aria-current={improvingWithGuide ? 'true' : undefined}
        data-guide-active={improvingWithGuide ? 'true' : undefined}
        className={`rounded-lg border p-3.5 transition-all ${
          improvingWithGuide
            ? 'border-signature bg-signature-tint ring-2 ring-signature shadow-sm'
            : needsClarification
            ? 'border-warning bg-warning-tint'
            : 'border-hairline'
        }`}
      >
        <div className="mb-1.5 flex items-center gap-2 flex-wrap">
          <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{label}</div>
          {improvingWithGuide && (
            <span className="rounded-full bg-signature-tint px-2 py-0.5 text-xs font-semibold text-signature-strong">
              Improving with guide
            </span>
          )}
          {!profileEditedFields.has(key) && cleanupPending && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.polishing}`}>Polishing…</span>
          )}
          {!profileEditedFields.has(key) && !cleanupPending && polished && !needsClarification && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.polished}`}>Polished</span>
          )}
          {!profileEditedFields.has(key) && !cleanupPending && cleanupFailed && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.cleanupError}`}>Original wording</span>
          )}
          {profileEditedFields.has(key) && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.edited}`}>Edited</span>
          )}
          {needsClarification && (
            <span className="rounded-full bg-warning-tint px-2 py-0.5 text-xs font-semibold text-warning">Needs client clarification</span>
          )}
        </div>

        {writing ? (
          <textarea
            ref={(el) => autoGrowTextarea(el)}
            value={displayValue}
            onChange={(e) => setValue(e.target.value)}
            rows={1}
            placeholder={isListField ? 'Comma-separated' : undefined}
            className="w-full border border-ink-border rounded-lg px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-signature resize-none overflow-hidden"
          />
        ) : (
          <p className="text-sm text-ink-secondary whitespace-pre-wrap">
            {isEmpty ? <span className="text-ink-muted italic">{emptyHint || 'Not found. Add your own.'}</span> : displayValue}
          </p>
        )}
        {key === 'certifications' && !isEmpty && (
          <div className="mt-2">
            {companyProfile.claimsReviewedAt ? (
              <div className="rounded-lg border border-success bg-success-tint px-3 py-2">
                <p className="text-xs font-medium text-ink">
                  Reviewed by {companyProfile.claimsReviewedByEmail || 'a manager on this account'} on{' '}
                  {new Date(companyProfile.claimsReviewedAt).toLocaleString()}
                </p>
                <p className="mt-0.5 text-xs text-ink-secondary">
                  Attested: the {companyProfile.certifications.length} claim{companyProfile.certifications.length === 1 ? '' : 's'} listed
                  above are factually correct, authorized for reps to say on a call, and current for this business.
                  Editing any claim clears this review and requires a new one before publishing.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-warning bg-warning-tint px-3 py-2">
                <p className="text-xs text-ink">
                  Compliance-sensitive. Before marking this reviewed, confirm the {claimsToVerifyCount} claim
                  {claimsToVerifyCount === 1 ? '' : 's'} listed above {claimsToVerifyCount === 1 ? 'is' : 'are'} factually correct,
                  authorized for reps to say on a call, and current for this business. This cannot be published until reviewed.
                </p>
                <button
                  type="button"
                  onClick={handleMarkClaimsReviewed}
                  disabled={markingClaimsReviewed || claimsCleanupPending}
                  className="mt-1.5 inline-flex min-h-[44px] items-center px-2 text-xs font-medium text-signature hover:text-signature-strong disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-signature rounded-lg"
                >
                  {markingClaimsReviewed
                    ? 'Marking reviewed…'
                    : claimsCleanupPending
                    ? 'Claims are still polishing…'
                    : `Mark ${claimsToVerifyCount} claim${claimsToVerifyCount === 1 ? '' : 's'} reviewed`}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 mt-2.5">
          {writing ? (
            <button
              onClick={async () => {
                if (!task) return;
                try {
                  await updateKnowledgeGeneratorCompanyProfile(task._id, key, raw);
                  setFieldWriting((prev) => ({ ...prev, [key]: false }));
                } catch (error) {
                  setPublishError(error instanceof Error ? error.message : 'Could not save this company detail.');
                }
              }}
              className="text-xs text-signature hover:text-signature-strong font-medium"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={() => openImproveProfileField(key, label)}
                className="text-xs text-signature hover:text-signature-strong font-medium"
              >
                Improve with guide
              </button>
              <button
                onClick={() => setFieldWriting((prev) => ({ ...prev, [key]: true }))}
                className="text-xs text-ink-muted hover:text-ink-secondary"
              >
                Edit manually
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  /** Sits above the section it belongs to, so the offer to undo appears
   *  where the row vanished from. Stacks if several were removed. */
  const renderUndoStrip = (section: 'objections' | 'faqs') => {
    const entries = removedSuggestions.filter((e) => e.section === section);
    if (!entries.length) return null;
    return (
      <div className="mb-2.5 space-y-1.5">
        {entries.map((entry) => (
          <div
            key={`${entry.item.kind}-${entry.item.tempId}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-surface-subtle px-3 py-2"
          >
            <span className="text-xs text-ink-secondary truncate">
              Removed “{entry.item.question || 'Untitled draft'}”
            </span>
            <button
              type="button"
              onClick={() => undoRemoveSuggestion(entry)}
              className="text-xs font-medium text-signature hover:text-signature-strong flex-shrink-0"
            >
              Undo
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderSuggestionRow = (item: ReviewSuggestion) => {
    const improvingWithGuide = activeGuideTarget?.type === 'suggestion'
      && activeGuideTarget.kind === item.kind
      && activeGuideTarget.tempId === item.tempId;
    const duplicatePairsForItem = duplicatePairsByTempId.get(item.tempId) || [];
    const canMergeIntoAnotherDraft = item.kind === 'objection' && (
      mergeableObjectionTempIds.size > (mergeableObjectionTempIds.has(item.tempId) ? 1 : 0)
    );
    return (
    <div
      key={`${item.kind}-${item.tempId}`}
      role="group"
      aria-label={`Draft response: ${item.question}`}
      aria-current={improvingWithGuide ? 'true' : undefined}
      data-guide-active={improvingWithGuide ? 'true' : undefined}
      className={`p-3.5 rounded-lg border transition-all ${
        improvingWithGuide
          ? 'border-signature bg-signature-tint ring-2 ring-signature shadow-sm'
          : hasUnresolvedUncertaintyLabel(item)
          ? 'border-warning bg-warning-tint'
          : item.pending
          ? 'border-info bg-info-tint'
          : item.synced
          ? 'border-hairline bg-surface-subtle opacity-75'
          : item.approved
          ? 'border-hairline bg-surface'
          : 'border-hairline bg-surface-subtle'
      }`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <input
          type="checkbox"
          // Without this a screen reader announces a bare "checkbox,
          // unchecked" with no way to tell which of thirty answers it
          // approves. The question is the only text that distinguishes them.
          aria-label={`Include in ${additiveReview ? 'publish' : 'starter set'}: ${item.question}`}
          checked={item.approved}
          disabled={item.synced || item.pending}
          onChange={() => toggleApproved(item.kind, item.tempId)}
          className="mt-1 w-4 h-4 text-signature rounded border-ink-border focus:ring-signature cursor-pointer disabled:cursor-not-allowed"
        />
        <div className="flex-1 min-w-[12rem]">
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.category}`}>
              {TOPIC_CATEGORY_LABELS[item.category as TopicCategory] || item.category}
            </span>
            {item.kind === 'websiteFaq' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.website}`}>From your website</span>
            )}
            {!['manual', 'voice'].includes(item.source || '') && (item.kind === 'aiGeneratedFaq' || item.kind === 'objection') && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.aiDrafted}`}>AI-drafted</span>
            )}
            {item.confidence === 'high' && <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.highConfidence}`}>High confidence</span>}
            {item.confidence === 'needs_review' && <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.needsReview}`}>Needs review</span>}
            {item.confidence === 'unscored' && <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.unscored}`}>Unscored</span>}
            {duplicateTempIds.has(item.tempId) && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.duplicate}`}>Possible duplicate</span>
            )}
            {item.voiceCaptured && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.voice}`}>Added by voice</span>
            )}
            {item.source === 'manual' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.voice}`}>Added manually</span>
            )}
            {['manual', 'voice'].includes(item.source || '') && !item.edited && item.styleCleanupStatus === 'pending' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.polishing}`}>Polishing…</span>
            )}
            {['manual', 'voice'].includes(item.source || '') && !item.edited && item.styleCleanupStatus === 'cleaned' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.polished}`}>Polished</span>
            )}
            {['manual', 'voice'].includes(item.source || '') && !item.edited && item.styleCleanupStatus === 'error' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.cleanupError}`}>Original wording</span>
            )}
            {item.pending && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.pending}`}>Needs answer</span>
            )}
            {item.edited && <span className={`px-2 py-0.5 text-xs rounded-full ${chipClass.edited}`}>Edited</span>}
            {improvingWithGuide && (
              <span className="rounded-full bg-signature-tint px-2 py-0.5 text-xs font-semibold text-signature-strong">
                Improving with guide
              </span>
            )}
            {hasUnresolvedUncertaintyLabel(item) && (
              <span className="rounded-full bg-warning-tint px-2 py-0.5 text-xs font-semibold text-warning">Needs client clarification</span>
            )}
          </div>
          <p className="text-sm font-medium text-ink">{item.question}</p>
          <p className="text-sm text-ink-secondary mt-1 whitespace-pre-wrap">
            {item.pending ? (
              <button
                type="button"
                aria-label={`Answer this ${item.kind === 'objection' ? 'objection' : 'FAQ'} next to save it`}
                onClick={() => requestGuideForSection(
                  item.kind === 'objection' ? 'objections' : 'faqs',
                  item.question,
                )}
                className="min-h-[44px] rounded-md text-left font-medium text-signature underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-signature"
              >
                Answer this next to save it.
              </button>
            ) : item.answer}
          </p>
          {item.variations.length > 0 && (
            <div className="mt-3 border-t border-hairline pt-2.5">
              <p className="text-xs font-semibold text-ink-muted">
                Variations ({item.variations.length})
              </p>
              <ul
                aria-label={`Variations for ${item.question}`}
                className="mt-1.5 space-y-1 text-xs text-ink-secondary"
              >
                {item.variations.map((variation) => (
                  <li key={variation.tempId} className="flex gap-2">
                    <span aria-hidden="true" className="text-ink-disabled">•</span>
                    <span>{variation.question}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.confidence && item.confidence !== 'high' && (
            <details className="mt-2 text-xs text-ink-secondary">
              <summary className="cursor-pointer font-medium text-ink-secondary">Why this needs review</summary>
              <p className="mt-1">{item.confidenceReason || 'The available evidence was incomplete or could not be checked for this draft.'}</p>
              {item.evidenceSources?.length ? <p className="mt-1">Evidence: {item.evidenceSources.join(', ')}</p> : null}
            </details>
          )}
          {duplicatePairsForItem.map((pair) => {
              const other = pair.a.tempId === item.tempId ? pair.b : pair.a;
              const key = duplicatePairKey(pair.a.tempId, pair.b.tempId);
              const acknowledged = acknowledgedDuplicatePairs.has(key);
              return (
                <div key={key} className="mt-2 rounded-lg border border-error bg-error-tint p-2.5 text-xs">
                  <p className="font-medium text-error">
                    {acknowledged ? 'Reviewed, kept both:' : 'This looks like a possible duplicate of:'}
                  </p>
                  <p className="mt-1 text-ink">&ldquo;{other.question}&rdquo;</p>
                  {!acknowledged && (
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={savingDuplicatePairKeys.has(key)}
                        onClick={() => { void acknowledgeDuplicatePair(pair); }}
                        className="min-h-[44px] rounded-lg border border-error bg-surface px-2 font-medium text-error hover:bg-error-tint focus:outline-none focus:ring-2 focus:ring-signature"
                      >
                        {savingDuplicatePairKeys.has(key) ? 'Saving…' : 'These are different. Keep both'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSuggestion(other.kind, other.tempId)}
                        className="min-h-[44px] rounded-lg border border-error bg-surface px-2 font-medium text-error hover:bg-error-tint focus:outline-none focus:ring-2 focus:ring-signature"
                      >
                        Keep this, remove the other
                      </button>
                      {item.kind === 'objection' && !item.pending && other.kind === 'objection' && mergeableObjectionTempIds.has(other.tempId) && (
                        <button
                          type="button"
                          onClick={() => openVariationMerge(item.tempId, other.tempId)}
                          className="min-h-[44px] rounded-control border border-signature bg-surface px-2 font-medium text-signature hover:bg-signature-tint focus:outline-none focus:ring-2 focus:ring-signature"
                        >
                          Add as variation
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        {!item.synced && !item.pending && (
          <div className="flex w-full pl-7 sm:w-auto sm:pl-0 items-center gap-1 flex-shrink-0">
            {canMergeIntoAnotherDraft && duplicatePairsForItem.length === 0 && (
              <button
                type="button"
                onClick={() => openVariationMerge(item.tempId)}
                className="min-h-[44px] text-xs text-signature hover:text-signature-strong font-medium px-1.5 focus:outline-none focus:ring-2 focus:ring-signature rounded-control"
              >
                Add as variation
              </button>
            )}
            <button
              onClick={() => openImproveSuggestion(item)}
              className="min-h-[44px] text-xs text-signature hover:text-signature-strong font-medium px-1.5 focus:outline-none focus:ring-2 focus:ring-signature rounded-lg"
            >
              Improve with guide
            </button>
            <button
              onClick={() => startEdit(item)}
              className="min-h-[44px] text-xs text-ink-muted hover:text-signature px-1.5 focus:outline-none focus:ring-2 focus:ring-signature rounded-lg"
            >
              Edit
            </button>
            <button
              onClick={() => removeSuggestion(item.kind, item.tempId)}
              className="min-h-[44px] text-xs text-ink-muted hover:text-error px-1.5 focus:outline-none focus:ring-2 focus:ring-signature rounded-lg"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderSuggestionList = (
    items: ReviewSuggestion[],
    section: 'objections' | 'faqs',
  ) => {
    const { visible, deferred } = partitionReviewSuggestions(items);
    return (
      <div className="space-y-2.5">
        {visible.map(renderSuggestionRow)}
        {deferred.length > 0 && (
          <details
            data-testid={`${section}-unchecked-drafts`}
            className="rounded-lg border border-hairline bg-surface-subtle"
          >
            <summary className="cursor-pointer px-3.5 py-3 text-sm font-medium text-ink-secondary focus:outline-none focus:ring-2 focus:ring-signature">
              Review {deferred.length} unchecked {section === 'objections' ? 'objection' : 'FAQ'} draft{deferred.length === 1 ? '' : 's'}
            </summary>
            <div className="space-y-2.5 border-t border-hairline p-2.5">
              {deferred.map(renderSuggestionRow)}
            </div>
          </details>
        )}
      </div>
    );
  };

  /** Keeps landmine review markup separate from ordinary Q&A rows. */
  const renderLandmineRow = (draft: KnowledgeGeneratorLandmineDraft) => {
    const needsClarification = hasUnresolvedUncertaintyLabel(draft);
    return (
    <div key={draft.tempId} className={`rounded-lg border p-2.5 ${needsClarification ? 'border-warning bg-warning-tint' : 'border-hairline bg-surface-subtle'}`}>
      <div className="flex items-start gap-2">
        <label className="flex items-start gap-2 min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Include landmine ${draft.trigger} in ${additiveReview ? 'publish' : 'launch'}`}
            checked={draft.approved}
            disabled={draft.synced || reviewingLandmineId === 'set' || savingSelectionKeys.has(`landmine:${draft.tempId}`)}
            onChange={() => toggleOnboardingApproved('landmine', draft.tempId)}
            className="mt-0.5 w-4 h-4 text-signature rounded border-ink-border"
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink">{draft.trigger}</div>
            <div className="mt-1 text-xs text-ink-secondary">
              {draft.landmineType === 'detractor' ? 'Conversation detractor' : 'Legal risk'},{' '}
              {(draft.priority || 'medium').charAt(0).toUpperCase()}{(draft.priority || 'medium').slice(1)} priority
            </div>
            <div className="text-xs text-ink-secondary mt-1 flex items-center gap-1.5 flex-wrap">
              {draft.synced ? 'Published and approved' : draft.reviewedAt ? 'Reviewed; not live yet' : 'Review required before publishing'}
              {needsClarification && (
                <span className="rounded-full bg-warning-tint px-1.5 py-0.5 font-semibold text-warning">
                  Needs client clarification
                </span>
              )}
            </div>
            {(draft.triggerVariations?.length || 0) > 0 && (
              <div className="mt-1 text-[11px]">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLandmineVariations(draft.tempId);
                  }}
                  className="text-ink-muted hover:text-ink-secondary underline decoration-dotted"
                >
                  {expandedLandmineVariations.has(draft.tempId)
                    ? 'Hide matching phrases'
                    : `${draft.triggerVariations?.length || 0} matching phrases included`}
                </button>
                {expandedLandmineVariations.has(draft.tempId) && (
                  <ul className="mt-1 space-y-0.5 list-disc list-inside text-ink-secondary">
                    {draft.triggerVariations?.map((phrase, i) => (
                      <li key={i}>{phrase}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {(draft.cannotSay?.length || 0) > 0 && (
              <div className="mt-1.5 text-[11px] text-ink-secondary">
                <span className="font-medium text-error">Avoid: </span>
                {draft.cannotSay.map((item) => item.point).join('; ')}
              </div>
            )}
            {(draft.canSay?.length || 0) > 0 && (
              <div className="mt-1 text-[11px] text-ink-secondary">
                <span className="font-medium text-success">Say instead: </span>
                {draft.canSay.map((item) => item.point).join('; ')}
              </div>
            )}
            {draft.quickTips && (
              <div className="mt-1 text-[11px] text-ink-secondary">
                <span className="font-medium">Tip: </span>{draft.quickTips}
              </div>
            )}
            {editingLandmineId === draft.tempId && (
              <div className="mt-3 space-y-3 rounded-xl border border-hairline bg-surface p-4" onClick={(event) => event.stopPropagation()}>
                <p className="text-sm font-semibold text-ink">Edit guidance</p>
                <label className="block text-sm font-medium text-ink-secondary">Primary detection phrase
                  <input value={landmineEdit.trigger} onChange={(event) => setLandmineEdit((value) => ({ ...value, trigger: event.target.value }))} className="mt-1.5 min-h-[44px] w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Cannot Say phrase
                  <textarea value={landmineEdit.cannotSay} onChange={(event) => setLandmineEdit((value) => ({ ...value, cannotSay: event.target.value }))} rows={2} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Can Say response
                  <textarea value={landmineEdit.canSay} onChange={(event) => setLandmineEdit((value) => ({ ...value, canSay: event.target.value }))} rows={2} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <label className="block text-sm font-medium text-ink-secondary">Detection phrases, one per line
                  <textarea value={landmineEdit.variations} onChange={(event) => setLandmineEdit((value) => ({ ...value, variations: event.target.value }))} rows={3} className="mt-1.5 w-full rounded-lg border border-ink-border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature" />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={addGeneratedVariations} className="kb-button kb-button-secondary min-h-[44px] px-4 text-sm">Generate variations</button>
                  <button type="button" disabled={savingLandmine} onClick={saveLandmineEdit} className="kb-button kb-button-primary min-h-[44px] px-4 text-sm">{savingLandmine ? 'Saving…' : 'Save guidance'}</button>
                  <button type="button" disabled={savingLandmine} onClick={() => setEditingLandmineId(null)} className="kb-button kb-button-tertiary min-h-[44px] px-3 text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </label>
        {!draft.synced && (
          <button type="button" aria-label={`Edit landmine ${draft.trigger}`} onClick={(event) => { event.stopPropagation(); startLandmineEdit(draft); }} className="min-h-[44px] px-2 text-[11px] font-medium text-signature hover:text-signature-strong focus:outline-none focus:ring-2 focus:ring-signature">Edit</button>
        )}
        {!draft.synced && (
          <button
            type="button"
            aria-label={`Remove landmine ${draft.trigger}`}
            onClick={(e) => {
              e.stopPropagation();
              removeLandmine(draft.tempId);
            }}
            className="min-h-[44px] text-[11px] text-ink-muted hover:text-error px-2 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-signature"
          >
            Remove
          </button>
        )}
      </div>
    </div>
    );
  };

  /** Competitor editing/persistence lives behind this card boundary. */
  const renderCompetitorRow = (draft: KnowledgeGeneratorCompetitorDraft) => (
    <CompetitorDraftCard
      key={draft.tempId}
      taskId={task!._id}
      draft={draft}
      onToggle={() => toggleOnboardingApproved('competitor', draft.tempId)}
      onRemove={() => removeCompetitor(draft.tempId)}
      onSaved={(updatedTask) => {
        setPublishError(null);
        setTask(updatedTask);
        setDraftCompetitors(unpublishedDraftItems(updatedTask.onboardingDraft?.competitors));
      }}
      onError={setPublishError}
      selectionContext={additiveReview ? 'publish' : 'starter set'}
      selectionDisabled={reviewingCompetitorId === 'set' || savingSelectionKeys.has(`competitor:${draft.tempId}`)}
    />
  );

  const currentVariationMergeSource = variationMergeRequest
    ? objections.find((objection) => objection.tempId === variationMergeRequest.sourceTempId) || null
    : null;
  const variationMergeCandidates = currentVariationMergeSource && !currentVariationMergeSource.synced && !currentVariationMergeSource.pending
    ? objections.filter((objection) => (
      objection.tempId !== currentVariationMergeSource.tempId && !objection.synced && !objection.pending
    ))
    : [];

  return (
    <div className="h-full w-full bg-paper flex flex-col">
      <div className="flex-shrink-0 bg-surface border-b border-hairline px-4 sm:px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="heading-4">
              {additiveReview ? 'Review knowledge additions' : 'We drafted your first knowledge base'}
            </h1>
            {selectionSaveError && (
              <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-sm text-error">
                <span>Choice was not saved: {selectionSaveError}</span>
                {selectionRetry && <button type="button" onClick={selectionRetry} className="min-h-[44px] rounded-lg border border-error px-3 font-semibold hover:bg-error-tint focus:outline-none focus:ring-2 focus:ring-signature">Retry save</button>}
              </div>
            )}
            {savingSelectionKeys.size > 0 && <p role="status" aria-live="polite" className="mt-2 text-sm text-info">Saving your choice…</p>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button onClick={handleClose} className="min-h-[44px] rounded-lg px-3 text-sm font-medium text-ink-secondary hover:bg-surface-subtle hover:text-ink focus:outline-none focus:ring-2 focus:ring-signature">
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden lg:flex">
      <aside aria-label="Smart setup guide" className="h-[44vh] bg-surface lg:h-full lg:w-[410px] lg:flex-shrink-0 lg:border-r border-hairline">
        <OnboardingWizard
          presentation="guide"
          request={wizardRequest}
          draftTaskId={task?._id}
          showCapturedList={false}
          autoBegin={!additiveReview}
          idleUntilRequested={additiveReview}
          onItemCaptured={handleGuideItemCaptured}
          onItemPending={handleGuideItemPending}
          onExit={onExit}
        />
      </aside>
      <div {...{ [REVIEW_SCROLLER_ATTR]: true }} className="h-full min-w-0 overflow-y-auto lg:flex-1">
      <div className="px-4 py-4 sm:px-6 sm:py-6">
        <div className="space-y-6">
          <DraftKnowledgeSearch items={selectedDraftPreviewItems} />
          {/* The launch snapshot is first-publication scaffolding. Once any
              content is live, this page is a focused additive draft. */}
          {!additiveReview && <section aria-labelledby="launch-snapshot-title" className="overflow-hidden rounded-2xl border border-hairline bg-surface shadow-sm">
            <div className="px-5 py-4 border-b border-hairline flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 id="launch-snapshot-title" className="text-base font-semibold text-ink">Launch snapshot</h2>
                <p className="text-sm text-ink-secondary mt-0.5">Review the selected content, then choose when to make it available to reps.</p>
                <p className="sr-only">{selectedObjectionsCount} objections and {selectedFaqsCount} FAQs selected for launch</p>
              </div>
              <span className="inline-flex w-fit items-center rounded-full bg-surface-subtle px-2.5 py-1 text-xs font-semibold text-ink-secondary">Live calls: off</span>
            </div>
            <div className="grid grid-cols-1 gap-3 bg-surface-subtle p-4 sm:grid-cols-2 xl:grid-cols-3">
              {[
                { label: 'Company details', status: hasUnverifiedClaims || companyProfileNeedsClarification ? 'Review required' : companyProfile?.include ? 'Ready' : 'Optional', detail: companyProfileNeedsClarification ? 'Clarify inferred company wording' : hasUnverifiedClaims ? `${claimsToVerifyCount} sensitive claim${claimsToVerifyCount === 1 ? '' : 's'} to check` : 'What you sell, who buys it, and how it works', action: () => selectAssistantSection('company_context'), tone: hasUnverifiedClaims || companyProfileNeedsClarification ? 'border-warning' : 'border-hairline' },
                { label: 'FAQs', status: `${selectedFaqsCount} selected`, detail: 'Short answers to common buyer questions', action: () => selectAssistantSection('faqs'), tone: 'border-hairline' },
                { label: 'Objections', status: `${selectedObjectionsCount} selected`, detail: 'Conversation-ready responses to buyer pushback', action: () => selectAssistantSection('objections'), tone: 'border-hairline' },
                { label: 'Landmines', status: selectedLandminesCount ? (landmineSetReviewed ? 'Reviewed' : 'Review required') : 'Optional', detail: `${selectedLandminesCount} selected phrase${selectedLandminesCount === 1 ? '' : 's'} and safe response${selectedLandminesCount === 1 ? '' : 's'}`, action: () => selectAssistantSection('landmines'), tone: selectedLandminesCount && !landmineSetReviewed ? 'border-warning' : 'border-hairline' },
                { label: 'Competitors', status: selectedCompetitorsCount ? (competitorSetReviewed ? 'Reviewed' : 'Review required') : 'Optional', detail: `${selectedCompetitorsCount} selected competitor profile${selectedCompetitorsCount === 1 ? '' : 's'}`, action: () => selectAssistantSection('competitors'), tone: selectedCompetitorsCount && !competitorSetReviewed ? 'border-warning' : 'border-hairline' },
                {
                  label: 'Sales playbook',
                  status: task?.onboardingDraft?.starterPlaybookTaskId
                    ? (includeStarterPlaybook ? (starterPlaybookReady ? 'Selected' : 'Review required') : 'Not selected')
                    : task?.onboardingDraft?.starterPlaybookError ? 'Needs attention' : 'Preparing',
                  detail: task?.onboardingDraft?.starterPlaybookError || (retryingStarterPlaybook
                    ? 'Retrying the missing draft; other content stays available.'
                    : includeStarterPlaybook
                    ? 'Selected audiences publish only with the final starter-set action.'
                    : 'The draft is saved but will not be included in this starter set.'),
                  action: task?.onboardingDraft?.starterPlaybookError ? retryStarterPlaybook : () => setShowPlaybookReview(true),
                  tone: task?.onboardingDraft?.starterPlaybookError || !starterPlaybookReady ? 'border-warning' : 'border-hairline',
                },
              ].map((card) => (
                <button key={card.label} type="button" onClick={card.action} className={`min-h-[116px] rounded-xl border ${card.tone} bg-surface p-4 text-left shadow-sm transition hover:border-signature hover:shadow focus:outline-none focus:ring-2 focus:ring-signature focus:ring-offset-2`}>
                  <span className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">{card.label}</span>
                  <span className="mt-2 block text-base font-semibold text-ink">{card.status}</span>
                  <span className="mt-1 block text-sm leading-5 text-ink-secondary">{card.detail}</span>
                  <span className="mt-3 block text-sm font-semibold text-signature">Open {card.label.toLowerCase()}</span>
                </button>
              ))}
            </div>
            <div className="border-t border-hairline px-5 py-4 text-sm">
              <p className="text-ink-secondary">Have existing material? Add it now to make the draft more specific. You can also continue without files.</p>
              {/* Even-width, stacked-below-the-copy grid instead of a
               *  same-row flex-wrap: these two labels are long enough that
               *  wrapping left them ragged and unevenly sized next to each
               *  other. Both open a modal now — neither should ever
               *  navigate an admin away from the draft they're reviewing. */}
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setShowBulkUploadModal(true)}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ink-border px-3 py-2 text-center font-semibold text-ink hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-signature"
                >
                  Import FAQ or objection spreadsheet
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStartPlaybookUpload(true);
                    setShowPlaybookReview(true);
                  }}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ink-border px-3 py-2 text-center font-semibold text-ink hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-signature"
                >
                  Add a .txt playbook or transcript
                </button>
              </div>
            </div>
          </section>}

          {/* Company profile draft — five focused cards, each with its own
              Improve with guide / Edit manually nudge, instead
              of one dense paragraph the admin has to know what to do with. */}
          {!additiveReview && companyProfile && (
            <div
              ref={(el) => { sectionRefs.current.company_context = el; }}
              className="bg-surface border border-hairline rounded-xl p-5 transition-shadow"
            >
              <div className="flex items-start gap-3 mb-3">
                <label className="pt-0.5 flex-shrink-0" title="Include company profile in starter set">
                  <input
                    type="checkbox"
                    aria-label="Include company profile in starter set"
                    checked={companyProfile.include}
                    onChange={(e) => setCompanyProfile({ ...companyProfile, include: e.target.checked })}
                    className="w-4 h-4 text-signature rounded border-ink-border"
                  />
                </label>
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => selectAssistantSection('company_context')}
                    className="text-sm font-semibold text-ink hover:text-signature"
                  >
                    Company profile draft
                  </button>
                  <div className="text-xs text-ink-muted mt-0.5">Included in starter set when checked.</div>
                </div>
              </div>
              <div className="space-y-2.5">{PROFILE_FIELDS.map(renderProfileField)}</div>
            </div>
          )}

          {/* Objections */}
          {(!additiveReview || objections.length > 0) && <section
            ref={(el) => { sectionRefs.current.objections = el; }}
            aria-label="Objections"
            className="overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm transition-shadow"
          >
            <div className="flex flex-col gap-2 border-b border-hairline bg-surface-subtle px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <button
                type="button"
                onClick={() => selectAssistantSection('objections')}
                className="text-sm font-semibold text-ink hover:text-signature"
              >
                  Objections, {selectedObjectionsCount} selected
                </button>
              </div>
              <button
                type="button"
                onClick={() => requestGuideForSection('objections')}
                className="text-xs text-signature hover:text-signature-strong font-medium"
              >
                Add with guide
              </button>
            </div>
            <div className="p-4">
              {renderUndoStrip('objections')}
              {renderSuggestionList(objections, 'objections')}
            </div>
          </section>}

          {/* FAQs */}
          {(!additiveReview || faqs.length > 0) && <section
            ref={(el) => { sectionRefs.current.faqs = el; }}
            aria-label="FAQs"
            className="overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm transition-shadow"
          >
            <div className="flex flex-col gap-2 border-b border-hairline bg-surface-subtle px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <button
                type="button"
                onClick={() => selectAssistantSection('faqs')}
                className="text-sm font-semibold text-ink hover:text-signature"
              >
                  FAQs, {selectedFaqsCount} selected
                </button>
              </div>
              <button
                type="button"
                onClick={() => requestGuideForSection('faqs')}
                className="text-xs text-signature hover:text-signature-strong font-medium"
              >
                Add with guide
              </button>
            </div>
            <div className="p-4">
              {renderUndoStrip('faqs')}
              {renderSuggestionList(faqs, 'faqs')}
            </div>
          </section>}

          <div className="space-y-5">
            {!additiveReview && <h2 className="text-sm font-semibold text-ink">Additional guidance</h2>}

            <div data-testid="supplemental-review-sections" className="space-y-3">
              {(!additiveReview || draftLandmines.length > 0) && <section
                ref={(el) => { sectionRefs.current.landmines = el; }}
                data-testid="landmines-review-section"
                aria-label="Landmines"
                className="overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-4 border-b border-hairline bg-surface-subtle px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-ink">Landmines</div>
                  </div>
                  <button
                    data-testid="add-landmine-guide"
                    type="button"
                    aria-label="Add landmine with guide"
                    onClick={() => requestGuideForSection('landmines')}
                    className="flex-shrink-0 text-xs font-medium text-signature hover:text-signature-strong"
                  >
                    Add with guide
                  </button>
                </div>
                <div className="px-4 pt-3 text-xs text-ink-secondary">
                  {selectedLandminesCount > 0
                    ? `${selectedLandminesCount} draft${selectedLandminesCount === 1 ? '' : 's'} selected`
                    : additiveReview
                    ? `${draftLandmines.length} draft${draftLandmines.length === 1 ? '' : 's'}`
                    : !landmineCounts || landmineCounts.total === 0
                    ? 'None added yet'
                    : landmineCounts.pending > 0
                    ? `${landmineCounts.total} added, ${landmineCounts.pending} pending review`
                    : `${landmineCounts.total} added`}
                </div>
                {removedLandmines.length > 0 && (
                  <div className="mt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                    {removedLandmines.map((entry) => (
                      <div
                        key={entry.item.tempId}
                        className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-surface-subtle px-2.5 py-1.5"
                      >
                        <span className="text-[11px] text-ink-secondary truncate">Removed “{entry.item.trigger}”</span>
                        <button
                          type="button"
                          onClick={() => undoRemoveLandmine(entry)}
                          className="text-[11px] font-medium text-signature hover:text-signature-strong flex-shrink-0"
                        >
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {draftLandmines.length > 0 && (
                  <div className="space-y-2 p-4 pt-3">
                    {selectedLandminesCount > 0 && (
                      <label className="flex items-start gap-3 rounded-xl border border-warning bg-warning-tint p-3 text-xs text-ink">
                        <input
                          type="checkbox"
                          checked={landmineSetReviewed}
                          disabled={reviewingLandmineId === 'set' || [...savingSelectionKeys].some((key) => key.startsWith('landmine:'))}
                          onChange={(event) => { void reviewLandmineSet(event.target.checked); }}
                          className="mt-0.5 h-5 w-5 rounded border-ink-border text-signature focus:ring-signature"
                          aria-label={`Acknowledge review of ${selectedLandminesCount} selected landmines`}
                        />
                        <span>
                          <strong>Review required.</strong> I’ve reviewed these {selectedLandminesCount} landmine{selectedLandminesCount === 1 ? '' : 's'} and confirm their phrases and suggested responses reflect our company’s guidance.
                        </span>
                      </label>
                    )}
                    {(showAllLandmines ? draftLandmines : draftLandmines.slice(0, 3)).map(renderLandmineRow)}
                    {draftLandmines.length > 3 && (
                      <button type="button" onClick={() => setShowAllLandmines((value) => !value)} className="kb-button kb-button-secondary min-h-[44px] w-full px-3 py-2 text-sm">
                        {showAllLandmines ? 'Show fewer landmines' : `Review all ${draftLandmines.length} landmines`}
                      </button>
                    )}
                  </div>
                )}
              </section>}

              {(!additiveReview || draftCompetitors.length > 0) && <section
                ref={(el) => { sectionRefs.current.competitors = el; }}
                data-testid="competitors-review-section"
                aria-label="Competitors"
                className="overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-4 border-b border-hairline bg-surface-subtle px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-ink">Competitors</div>
                  </div>
                  <button
                    type="button"
                    aria-label="Add competitor with guide"
                    onClick={() => requestGuideForSection('competitors')}
                    className="flex-shrink-0 text-xs font-medium text-signature hover:text-signature-strong"
                  >
                    Add with guide
                  </button>
                </div>
                <div className="px-4 pt-3 text-xs text-ink-secondary">
                  {selectedCompetitorsCount > 0
                    ? `${selectedCompetitorsCount} draft${selectedCompetitorsCount === 1 ? '' : 's'} selected`
                    : additiveReview
                    ? `${draftCompetitors.length} draft${draftCompetitors.length === 1 ? '' : 's'}`
                    : !competitorCounts || competitorCounts.total === 0
                    ? 'None added yet'
                    : competitorCounts.pending > 0
                    ? `${competitorCounts.total} added, ${competitorCounts.pending} pending cleanup`
                    : `${competitorCounts.total} added`}
                </div>
                {removedCompetitors.length > 0 && (
                  <div className="mt-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                    {removedCompetitors.map((entry) => (
                      <div
                        key={entry.item.tempId}
                        className="flex items-center justify-between gap-3 rounded-lg border border-hairline bg-surface-subtle px-2.5 py-1.5"
                      >
                        <span className="text-[11px] text-ink-secondary truncate">Removed “{entry.item.name}”</span>
                        <button
                          type="button"
                          onClick={() => undoRemoveCompetitor(entry)}
                          className="text-[11px] font-medium text-signature hover:text-signature-strong flex-shrink-0"
                        >
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {draftCompetitors.length > 0 && (
                  <div className="space-y-2 p-4 pt-3">
                    {selectedCompetitorsCount > 0 && (
                      <label className="flex items-start gap-3 rounded-xl border border-info bg-info-tint p-3 text-xs text-ink">
                        <input
                          type="checkbox"
                          checked={competitorSetReviewed}
                          disabled={reviewingCompetitorId === 'set' || [...savingSelectionKeys].some((key) => key.startsWith('competitor:'))}
                          onChange={(event) => { void reviewCompetitorSet(event.target.checked); }}
                          className="mt-0.5 h-5 w-5 rounded border-ink-border text-signature focus:ring-signature"
                          aria-label={`Acknowledge review of ${selectedCompetitorsCount} selected competitors`}
                        />
                        <span>
                          <strong>Review required.</strong> I’ve reviewed these {selectedCompetitorsCount} competitor{selectedCompetitorsCount === 1 ? '' : 's'} and confirm their positioning reflects our company’s guidance.
                        </span>
                      </label>
                    )}
                    {(showAllCompetitors ? draftCompetitors : draftCompetitors.slice(0, 3)).map(renderCompetitorRow)}
                    {draftCompetitors.length > 3 && (
                      <button type="button" onClick={() => setShowAllCompetitors((value) => !value)} className="kb-button kb-button-secondary min-h-[44px] w-full px-3 py-2 text-sm">
                        {showAllCompetitors ? 'Show fewer competitors' : `Review all ${draftCompetitors.length} competitors`}
                      </button>
                    )}
                  </div>
                )}
              </section>}

              {(!additiveReview || starterPlaybookNeedsReview) && <div className="border border-hairline rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-ink">Playbooks</div>
                    <p className="text-xs text-ink-muted mt-1">
                      Review the starter playbook now. It becomes available to reps only after its existing publication prerequisites are met.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPlaybookReview(true)}
                    className="flex-shrink-0 text-xs font-medium text-signature hover:text-signature-strong focus:outline-none focus:ring-2 focus:ring-signature"
                  >
                    Review playbook
                  </button>
                </div>
              </div>}
            </div>
          </div>

        </div>
      </div>
      </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-hairline bg-white/95 px-4 py-2.5 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:px-6">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              {publishing ? (
                <p role="status" aria-live="polite" className="text-sm text-ink-secondary">
                  {additiveReview ? 'Publishing selected additions…' : 'Finishing the wording and publishing your starter set…'}
                </p>
              ) : publishError ? (
                <p role="alert" className="text-sm text-error">{publishError}</p>
              ) : (
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1" aria-live="polite">
                  {!atLeastOneItemSelected ? (
                    <p className="text-sm text-ink-secondary">Select additions above to publish.</p>
                  ) : blockingChecklistCount > 0 ? (
                    <>
                      <p className="text-sm text-ink-secondary">
                        <span className="font-semibold text-ink">{selectionSummary} selected.</span>{' '}
                        {blockingChecklistCount} required review{blockingChecklistCount === 1 ? '' : 's'} remaining.
                      </p>
                      <button
                        type="button"
                        aria-expanded={showPublishChecklist}
                        aria-controls="publish-readiness-details"
                        onClick={() => setShowPublishChecklist((visible) => !visible)}
                        className="rounded-md px-1.5 py-1 text-xs font-semibold text-signature hover:bg-signature-tint focus:outline-none focus:ring-2 focus:ring-signature"
                      >
                        {showPublishChecklist ? 'Hide checks' : 'View checks'}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-ink-secondary">
                      <span className="font-semibold text-ink">{selectionSummary} selected.</span> Ready to publish.
                    </p>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPublishConfirmation(true)}
              disabled={!canPublish}
              className="kb-button kb-button-primary min-h-[40px] w-full flex-shrink-0 px-4 py-2 text-sm sm:w-auto"
            >
              {publishing ? 'Publishing…' : additiveReview ? 'Publish selected additions' : 'Publish starter set'}
            </button>
          </div>

          {!publishing && !publishError && atLeastOneItemSelected && blockingChecklistCount > 0 && showPublishChecklist && (
            <div id="publish-readiness-details" className="mt-2 border-t border-hairline pt-2">
              <ul aria-label="Required reviews" className="space-y-1.5">
                {publishBlockers.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        onClick={item.onClick}
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-transparent bg-warning-tint px-3 py-2 text-left text-xs text-ink hover:border-warning focus:outline-none focus:ring-2 focus:ring-signature"
                      >
                        <span>{item.detail}</span>
                        <span className="shrink-0 font-semibold">Review</span>
                      </button>
                    </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {showBulkUploadModal && task && (
        <BulkUploadModal
          draftTaskId={task._id}
          onClose={() => setShowBulkUploadModal(false)}
          onJobSettled={() => {
            // The rows land server-side asynchronously. Re-read the task so
            // newly added objections/FAQs appear without changing any saved
            // selection choices.
            if (!task) return;
            getKnowledgeGeneratorTask(task._id)
              .then((refreshed) => applyTask(refreshed, false))
              .catch((err) => console.error('[SeededOnboardingReview] Failed to refresh the draft after a spreadsheet import:', err));
          }}
        />
      )}

      {showPlaybookReview && (
        <PlaybookIngestionReview
          isOpen
          initialTaskId={task?.onboardingDraft?.starterPlaybookTaskId}
          sourceKnowledgeTaskId={task?._id}
          initialNewDraftMode={startPlaybookUpload ? 'upload' : undefined}
          allowGenerateFromPublishedKb={false}
          // Always 'starter': playbook inclusion is decided by the checkbox
          // below and published through the outer Publish action, whether
          // this is the first draft or a later additive one. 'standalone'
          // (immediate, independent publish) stays reserved for the general
          // Playbooks page, which has no outer publish step to bundle into.
          publicationMode="starter"
          includedInStarterSet={includeStarterPlaybook}
          onIncludedInStarterSetChange={handleStarterPlaybookSelection}
          onClose={() => {
            setShowPlaybookReview(false);
            setStartPlaybookUpload(false);
          }}
          onDraftSaved={(saved) => {
            setStarterPlaybook(saved);
            if (task) {
              getKnowledgeGeneratorTask(task._id)
                .then((refreshed) => applyTask(refreshed, false))
                .catch((err) => console.error('[SeededOnboardingReview] Failed to refresh the draft after replacing its starter playbook:', err));
            }
          }}
          onSynced={() => undefined}
          setError={(message) => setSelectionSaveError(message)}
          setSuccess={(message) => setPlaybookMessage(message)}
        />
      )}
      {playbookMessage && (
        <div role="status" aria-live="polite" className="fixed bottom-24 left-1/2 z-[1800] -translate-x-1/2 rounded-lg bg-ink px-4 py-3 text-sm text-white shadow-xl">
          {playbookMessage}
        </div>
      )}

      {showPublishConfirmation && (
        <div className="modal-backdrop kb-modal-overlay z-[1700]">
          <div role="dialog" aria-modal="true" aria-labelledby="publish-confirmation-title" className="modal-panel kb-dialog w-full max-w-md p-5">
            <h2 id="publish-confirmation-title" className="heading-4 kb-dialog-title">
              {additiveReview ? 'Publish selected additions?' : 'Publish this starter set?'}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-secondary">
              This makes {selectedObjectionsCount} objection{selectedObjectionsCount === 1 ? '' : 's'} and {selectedFaqsCount} FAQ{selectedFaqsCount === 1 ? '' : 's'} available on live calls{companyProfile?.include ? ', along with the company profile' : ''}.
              {selectedLandminesCount > 0 && ` It also publishes ${selectedLandminesCount} selected landmine${selectedLandminesCount === 1 ? '' : 's'} after wording review.`}
              {selectedCompetitorsCount > 0 && ` It also publishes ${selectedCompetitorsCount} selected competitor${selectedCompetitorsCount === 1 ? '' : 's'} after profile review.`}
              {starterPlaybookSelected && ` It also publishes the starter playbook with ${selectedPlaybookAudienceCount} selected audience${selectedPlaybookAudienceCount === 1 ? '' : 's'}.`}
            </p>
            {hasUnverifiedClaims && (
              <p className="mt-3 rounded-lg border border-warning bg-warning-tint px-3 py-2 text-sm text-ink">
                {claimsToVerifyCount} company claim{claimsToVerifyCount === 1 ? '' : 's'} still need verification. Publish only language your team has approved.
              </p>
            )}
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button type="button" onClick={() => setShowPublishConfirmation(false)} className="kb-button kb-button-secondary px-4 py-2.5 text-sm">Keep reviewing</button>
              <button type="button" disabled={publishing} onClick={handlePublish} className="kb-button kb-button-primary px-4 py-2.5 text-sm">{publishing ? 'Publishing…' : `Publish ${selectionSummary || 'selection'}`}</button>
            </div>
          </div>
        </div>
      )}

      {variationMergeRequest && (
        <ObjectionVariationMergeDialog
          key={`${variationMergeRequest.sourceTempId}:${variationMergeRequest.initialTargetTempId || ''}`}
          source={currentVariationMergeSource || variationMergeRequest.source}
          candidates={variationMergeCandidates}
          initialTargetTempId={variationMergeRequest.initialTargetTempId}
          isSaving={isMergingVariation}
          error={variationMergeError}
          onClose={() => {
            if (isMergingVariation) return;
            setVariationMergeRequest(null);
            setVariationMergeError(null);
          }}
          onMerge={(targetTempId) => { void handleVariationMerge(targetTempId); }}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <div
          className="modal-backdrop kb-modal-overlay z-[1400]"
          // Clicking the backdrop dismisses, the same as Escape — but only
          // the backdrop itself, never a click that started inside the card.
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-suggestion-title"
            className="modal-panel kb-dialog w-full max-w-lg"
          >
            <div className="p-4 border-b border-hairline">
              <h3 id="edit-suggestion-title" className="heading-4 kb-dialog-title">Edit suggestion</h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="edit-suggestion-question">
                  Question
                </label>
                <textarea
                  id="edit-suggestion-question"
                  // Puts the caret inside the dialog on open, so keyboard
                  // focus follows the thing that just appeared instead of
                  // staying on the Edit button behind the overlay.
                  autoFocus
                  value={editForm.question}
                  onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-signature"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="edit-suggestion-answer">
                  Answer
                </label>
                <textarea
                  id="edit-suggestion-answer"
                  value={editForm.answer}
                  onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-signature"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1" id="edit-suggestion-category-label">
                  Category
                </label>
                <Dropdown
                  id="edit-suggestion-category"
                  labelledBy="edit-suggestion-category-label"
                  width="field"
                  value={editForm.category}
                  onChange={(category) => setEditForm({ ...editForm, category })}
                  options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: TOPIC_CATEGORY_LABELS[c] }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="edit-suggestion-variations">
                  Variations, one per line
                </label>
                <textarea
                  id="edit-suggestion-variations"
                  value={editForm.variations}
                  onChange={(e) => setEditForm({ ...editForm, variations: e.target.value })}
                  rows={4}
                  placeholder="Add alternate ways a buyer might phrase this"
                  className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-signature"
                />
                <p className="mt-1 text-xs text-ink-muted">
                  Edit a line, delete it to remove the variation, or add a new line.
                </p>
                {editGenerationError && (
                  <p role="alert" className="mt-2 text-sm text-error">{editGenerationError}</p>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-hairline flex items-center justify-end gap-3">
              <div className="mr-auto flex items-center gap-3">
                {!editIsSaveable && (
                  <span className="text-xs text-ink-muted">
                    Both a question and an answer are needed before this can be saved.
                  </span>
                )}
                {editing.kind !== 'objection' && (
                  <button
                    type="button"
                    onClick={generateEditVariations}
                    disabled={!editIsSaveable || generatingEditVariations}
                    className="kb-button kb-button-secondary px-4 py-2"
                  >
                    {generatingEditVariations ? 'Generating…' : 'Generate variations'}
                  </button>
                )}
              </div>
              <button onClick={() => setEditing(null)} className="kb-button kb-button-secondary px-4 py-2">
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={!editIsSaveable || generatingEditVariations}
                className="kb-button kb-button-primary px-4 py-2"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeededOnboardingReview;
